import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import 'react-resizable/css/styles.css';
import { DragDropContext } from '@hello-pangea/dnd';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import DeleteConfirmModal from 'components/DeleteConfirmModal';
import DropDownMenu from 'components/DropDownMenu';
import SwimlaneRefactored from '../Swimlane/refactored';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { getBudgetTemplateName, isAicpTemplateName } from 'utils/constants';
import { getDetailsSheet, prepareDropDownOptions } from 'utils/common';
import { DeleteOutlineIcon, ExpandIcon, Icon } from 'utils/icons';
import { useBudgetMutation } from 'utils/api/mutations';
import API_ENDPOINTS from 'utils/api/endpoints';
import { formatDateTime, toastStyle } from 'utils/helper';
import {
  BUDGET_WORKSPACE_CACHE_TIME_MS,
  BUDGET_WORKSPACE_STALE_TIME_MS,
  getSwimlaneQueryKey,
} from 'utils/budgetWorkspaceCache';
/*
import { ReactComponent as ArrowBackIcon } from 'assets/images/arrow-back.svg';
import { ReactComponent as ArrowForwardIcon } from 'assets/images/arrow-forward.svg';
import { ReactComponent as FastRewindIcon } from 'assets/images/fast-rewind.svg';
*/
import DetailedViewRefactored from '../DetailedView/refactored';
import AiInsightsPanel from '../DetailedView/AiInsightsPanel';
import BudgetSearchOverlay from '../BudgetSearchOverlay';
import {
  getHistoryEventIds,
  getRedoThroughEventIds,
  getUndoThroughEventIds,
} from './historyActions';

const CHANGE_FIELD_LABELS = {
  name: 'Name',
  item: 'Item',
  quantity: 'Quantity',
  unit: 'Unit',
  rate: 'Rate',
  multiplier: 'Multiplier',
  multiplier_unit: 'Multiplier Unit',
  item_total: 'Item Total',
  fringe_total: 'Fringe Total',
  total_cost: 'Total',
  cost: 'Cost',
  comment: 'Notes',
  position: 'Position',
  charge_type: 'Charge Type',
  percentage: 'Percentage',
  flat_amount: 'Flat Amount',
  budget_version: 'Budget Version',
};

const CHANGE_FIELD_ORDER = [
  'item',
  'name',
  'quantity',
  'unit',
  'rate',
  'multiplier',
  'multiplier_unit',
  'item_total',
  'fringe_total',
  'total_cost',
  'cost',
  'percentage',
  'flat_amount',
  'charge_type',
  'comment',
  'position',
];

const CURRENCY_CHANGE_FIELDS = new Set([
  'cost',
  'flat_amount',
  'fringe_total',
  'item_total',
  'rate',
  'total_cost',
]);

const NUMBER_CHANGE_FIELDS = new Set([
  'multiplier',
  'position',
  'quantity',
  'script_pages',
  'shooting_days',
]);

const PERCENTAGE_CHANGE_FIELDS = new Set(['percentage']);
const HIDDEN_CHANGE_FIELDS = new Set(['is_placeholder', 'position']);
const HISTORY_GROUP_WINDOW_MS = 10000;
const CATEGORY_DROPPABLE_PREFIX = 'category-column';
const CATEGORY_DRAG_AUTO_SCROLLER_OPTIONS = { disabled: true };
const CATEGORY_NEST_HOLD_MS = 1000;
const CATEGORY_NEST_TARGET_ZONE_RATIO = 0.84;
const CATEGORY_NEST_TARGET_VERTICAL_TOLERANCE = 8;
const CATEGORY_NEST_TARGET_HORIZONTAL_TOLERANCE = 10;
const AI_RATE_CARD_REFRESH_DELAY_MS = 700;
const AI_RATE_CARD_MAX_FOCUS_ITEMS = 3;
const NAV_WIDTH_UPDATE_DELAY_MS = 80;
const VIEWPORT_RESIZE_SETTLE_MS = 160;
const CATEGORY_CHILD_KEYS = [
  'budget_swimlane_lineitem',
  'bs_lineitem_children',
  'children',
  'line_items',
];

const buildCategoryDroppableId = swimlaneIndex =>
  `${CATEGORY_DROPPABLE_PREFIX}-${swimlaneIndex}`;

const getCategoryColumnIndex = droppableId => {
  if (!String(droppableId || '').startsWith(`${CATEGORY_DROPPABLE_PREFIX}-`)) {
    return null;
  }

  const columnIndex = Number(
    String(droppableId).replace(`${CATEGORY_DROPPABLE_PREFIX}-`, '')
  );

  return Number.isInteger(columnIndex) ? columnIndex : null;
};

const getCategorySectionId = item => {
  const section = item?.section;
  if (section === undefined || section === null || section === '') {
    return null;
  }

  return section?.id?.toString?.() || section?.toString();
};

const getColumnRows = columnRef => {
  const rows = columnRef?.getRows?.();
  if (Array.isArray(rows)) return rows;

  return (columnRef?.items?.() || []).map(item => ({ type: 'item', item }));
};

const getCategoryFromDragLocation = (columnRef, location, draggableId) => {
  const rows = getColumnRows(columnRef);
  const row =
    rows.find(
      candidate =>
        candidate?.type === 'item' && candidate?.draggableId === draggableId
    ) || rows[location?.index];
  return row?.type === 'item' ? row.item : null;
};

const getCategoryFromDraggableId = (columnRef, draggableId) => {
  const rows = getColumnRows(columnRef);
  const row = rows.find(
    candidate =>
      candidate?.type === 'item' && candidate?.draggableId === draggableId
  );

  return row?.type === 'item' ? row.item : null;
};

const getCategoryNestTargetKey = combine => {
  if (!combine?.droppableId || !combine?.draggableId) return '';
  return `${combine.droppableId}:${combine.draggableId}`;
};

const getDragPointerClientPoint = event => {
  const touch = event?.touches?.[0] || event?.changedTouches?.[0];
  if (touch) {
    return { x: touch.clientX, y: touch.clientY };
  }

  if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
    return { x: event.clientX, y: event.clientY };
  }

  return null;
};

const isPointInsideCategoryNestZone = (point, rect) => {
  if (!rect?.width || !rect?.height) return false;
  if (
    point.x < rect.left - CATEGORY_NEST_TARGET_HORIZONTAL_TOLERANCE ||
    point.x > rect.right + CATEGORY_NEST_TARGET_HORIZONTAL_TOLERANCE
  ) {
    return false;
  }

  const outerZoneRatio = (1 - CATEGORY_NEST_TARGET_ZONE_RATIO) / 2;
  const nestTop = rect.top + rect.height * outerZoneRatio;
  const nestBottom = rect.bottom - rect.height * outerZoneRatio;

  return (
    point.y >= nestTop - CATEGORY_NEST_TARGET_VERTICAL_TOLERANCE &&
    point.y <= nestBottom + CATEGORY_NEST_TARGET_VERTICAL_TOLERANCE
  );
};

const getCategoryNestTargetFromPoint = ({ point, draggedDraggableId }) => {
  if (!point || !draggedDraggableId || typeof document === 'undefined') {
    return null;
  }

  const itemElements = Array.from(
    document.querySelectorAll('.swimlane-item[data-category-draggable-id]')
  );

  for (const itemElement of itemElements) {
    const draggableId = itemElement.dataset.categoryDraggableId;
    if (!draggableId || draggableId === draggedDraggableId) continue;

    const droppableId = itemElement.dataset.categoryDroppableId;
    if (!droppableId) continue;
    if (getCategoryColumnIndex(droppableId) === null) continue;

    const rect = itemElement.getBoundingClientRect();
    if (!isPointInsideCategoryNestZone(point, rect)) continue;

    return { droppableId, draggableId };
  }

  return null;
};

const getCategoryChildCount = item => {
  const explicitCount = Number(item?.children_count);
  if (Number.isFinite(explicitCount)) return explicitCount;

  return CATEGORY_CHILD_KEYS.reduce((largestCount, key) => {
    const children = item?.[key];
    if (!Array.isArray(children)) return largestCount;
    return Math.max(largestCount, children.length);
  }, 0);
};

const getDestinationParentId = column => {
  if (!column || column.type === 'swimlane') return null;
  return column.selectedItem?.id ?? null;
};

const getDestinationSectionId = (rows, destinationIndex) => {
  for (let index = destinationIndex; index < rows.length; index += 1) {
    const row = rows[index];
    if (row?.type === 'section') {
      return row.section?.id ?? null;
    }
  }

  return null;
};

const getDestinationPosition = ({
  rows,
  destinationIndex,
  destinationSectionId,
  isTopLevel,
}) => {
  if (!isTopLevel) return destinationIndex;

  const targetSectionId =
    destinationSectionId === null || destinationSectionId === undefined
      ? null
      : destinationSectionId.toString();

  return rows
    .slice(0, destinationIndex)
    .filter(
      row =>
        row?.type === 'item' &&
        getCategorySectionId(row.item) === targetSectionId
    ).length;
};

const getTopLevelInsertIndex = ({
  rows,
  destinationPosition,
  destinationSectionId,
}) => {
  const targetSectionId =
    destinationSectionId === null || destinationSectionId === undefined
      ? null
      : destinationSectionId.toString();
  let categoryPosition = 0;

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];

    if (row?.type === 'section') {
      if (getCategorySectionId({ section: row.section }) === targetSectionId) {
        return index;
      }
      continue;
    }

    if (row?.type !== 'item') continue;
    if (getCategorySectionId(row.item) !== targetSectionId) continue;
    if (categoryPosition === destinationPosition) return index;
    categoryPosition += 1;
  }

  return rows.length;
};

const withNormalizedPositions = items =>
  items.map((item, position) => ({
    ...item,
    position,
  }));

const rowsToPositionedCategoryItems = rows => {
  const pendingRows = [];
  const positionedItems = [];

  const flushRows = sectionId => {
    pendingRows.forEach((row, position) => {
      positionedItems.push({
        ...row.item,
        section: sectionId,
        position,
      });
    });
    pendingRows.length = 0;
  };

  rows.forEach(row => {
    if (row?.type === 'section') {
      flushRows(row.section?.id ?? null);
      return;
    }

    if (row?.type === 'item') {
      pendingRows.push(row);
    }
  });

  flushRows(null);

  return positionedItems;
};

const getOptimisticSourceItems = ({ items, movedItem, isTopLevel, rows }) => {
  const nextItems = items.filter(
    item => item?.id?.toString() !== movedItem.id?.toString()
  );

  if (!isTopLevel) {
    return withNormalizedPositions(nextItems);
  }

  return rowsToPositionedCategoryItems(
    (rows || []).filter(
      row =>
        row?.type !== 'item' ||
        row.item?.id?.toString() !== movedItem.id?.toString()
    )
  );
};

const getOptimisticDestinationItems = ({
  destinationItems,
  destinationPosition,
  destinationRows,
  isTopLevel,
  movedItem,
  optimisticMovedItem,
}) => {
  const nextItems = destinationItems.filter(
    item => item?.id?.toString() !== movedItem.id?.toString()
  );

  if (!isTopLevel) {
    const orderedItems = [...nextItems];
    orderedItems.splice(destinationPosition, 0, optimisticMovedItem);
    return withNormalizedPositions(orderedItems);
  }

  const optimisticRows = (destinationRows || []).filter(
    row =>
      row?.type !== 'item' ||
      row.item?.id?.toString() !== movedItem.id?.toString()
  );
  const optimisticInsertIndex = getTopLevelInsertIndex({
    rows: optimisticRows,
    destinationPosition,
    destinationSectionId: optimisticMovedItem.section,
  });
  optimisticRows.splice(optimisticInsertIndex, 0, {
    type: 'item',
    item: optimisticMovedItem,
  });

  return rowsToPositionedCategoryItems(optimisticRows);
};

const getDestinationSwimlaneId = column => {
  if (!column || column.type === 'swimlane') return null;
  if (column.type === 'line-item') return column.selectedItem?.id ?? null;
  return column.selectedItem?.swimlane ?? null;
};

const idsMatch = (a, b) => {
  if (a === null || a === undefined || a === '') {
    return b === null || b === undefined || b === '';
  }

  return a?.toString() === b?.toString();
};

const getOpenColumnIndexForCategory = (columns = [], categoryId) => {
  if (categoryId === null || categoryId === undefined || categoryId === '') {
    return -1;
  }

  return columns.findIndex(column =>
    idsMatch(column?.selectedItem?.id, categoryId)
  );
};

const formatChangeField = field => {
  if (CHANGE_FIELD_LABELS[field]) return CHANGE_FIELD_LABELS[field];
  return String(field || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

const toNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return null;

  const sanitized = String(value).replace(/[$,%\s,]/g, '');
  if (!sanitized) return null;

  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundToPlaces = (value, places) => {
  const factor = 10 ** places;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

const formatPlainNumber = (value, maximumFractionDigits = 4) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(value);

const getSafeCurrencyCode = currency => {
  const currencyCode =
    String(currency || '')
      .trim()
      .toUpperCase() || 'USD';

  try {
    new Intl.NumberFormat('en-US', {
      currency: currencyCode,
      style: 'currency',
    }).format(0);

    return currencyCode;
  } catch (error) {
    return 'USD';
  }
};

const formatCurrencyChangeValue = (value, currency = 'USD') =>
  new Intl.NumberFormat('en-US', {
    currency: getSafeCurrencyCode(currency),
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(value);

const formatChangeValue = (value, field, currency) => {
  if (value === null || value === undefined || value === '') return 'blank';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  const numericValue = toNumber(value);
  if (numericValue !== null) {
    if (CURRENCY_CHANGE_FIELDS.has(field)) {
      return formatCurrencyChangeValue(numericValue, currency);
    }

    if (PERCENTAGE_CHANGE_FIELDS.has(field)) {
      return `${formatPlainNumber(numericValue, 2)}%`;
    }

    if (NUMBER_CHANGE_FIELDS.has(field)) {
      return formatPlainNumber(numericValue);
    }
  }

  return String(value);
};

const isMeaningfulChange = (field, values) => {
  if (HIDDEN_CHANGE_FIELDS.has(field)) return false;

  const oldValue = values?.old;
  const newValue = values?.new;

  if (oldValue === newValue) return false;

  const oldNumber = toNumber(oldValue);
  const newNumber = toNumber(newValue);

  if (oldNumber !== null && newNumber !== null) {
    if (CURRENCY_CHANGE_FIELDS.has(field)) {
      return roundToPlaces(oldNumber, 2) !== roundToPlaces(newNumber, 2);
    }

    if (PERCENTAGE_CHANGE_FIELDS.has(field)) {
      return roundToPlaces(oldNumber, 2) !== roundToPlaces(newNumber, 2);
    }

    if (NUMBER_CHANGE_FIELDS.has(field)) {
      return roundToPlaces(oldNumber, 4) !== roundToPlaces(newNumber, 4);
    }
  }

  return (
    formatChangeValue(oldValue, field) !== formatChangeValue(newValue, field)
  );
};

const getMeaningfulChanges = event =>
  Object.fromEntries(
    Object.entries(event?.changes || {}).filter(([field, values]) =>
      isMeaningfulChange(field, values)
    )
  );

const isBlankChangeValue = value =>
  value === null || value === undefined || value === '';

const sortChangeEntries = entries =>
  entries.sort(([fieldA], [fieldB]) => {
    const indexA = CHANGE_FIELD_ORDER.indexOf(fieldA);
    const indexB = CHANGE_FIELD_ORDER.indexOf(fieldB);

    if (indexA === -1 && indexB === -1) {
      return fieldA.localeCompare(fieldB);
    }

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

const getChangeEventSignature = event =>
  JSON.stringify({
    action: event?.action,
    actor: event?.actor,
    changes: event?.changes || {},
    objectId: event?.object_id,
    objectPath: event?.object_path,
    objectType: event?.object_type,
    isUndone: event?.is_undone,
  });

const getPathParts = event =>
  String(event?.object_path || event?.object_label || '')
    .split(' > ')
    .map(part => part.trim())
    .filter(Boolean);

const getHistoryParentPath = event => {
  const pathParts = getPathParts(event);
  if (pathParts.length <= 1) return '';

  return pathParts.slice(0, -1).join(' > ');
};

const isBlankDetailRowEvent = event => {
  const text = `${event?.object_path || ''} ${event?.object_label || ''}`;
  return text.includes('(blank detail row)');
};

const dedupeConsecutiveChangeEvents = events => {
  let previousSignature = '';

  return events.filter(event => {
    const signature = getChangeEventSignature(event);
    if (signature === previousSignature) return false;

    previousSignature = signature;
    return true;
  });
};

const getHistoryGroupKey = event =>
  [
    event?.actor || event?.actor_email || event?.actor_name || '',
    event?.object_type || '',
    event?.object_id || event?.object_path || event?.object_label || '',
  ].join(':');

const getHistoryParentGroupKey = event =>
  [
    event?.actor || event?.actor_email || event?.actor_name || '',
    event?.object_type || '',
    getHistoryParentPath(event),
  ].join(':');

const getEventTimestamp = event => {
  const timestamp = new Date(event?.created_on).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const canGroupHistoryEvents = (event, group) => {
  const newestEvent = group?.[0];
  if (!event || !newestEvent) return false;

  const timeDelta = Math.abs(
    getEventTimestamp(newestEvent) - getEventTimestamp(event)
  );

  if (timeDelta > HISTORY_GROUP_WINDOW_MS) return false;

  if (getHistoryGroupKey(event) === getHistoryGroupKey(newestEvent)) {
    return true;
  }

  const hasBlankDetailRow = isBlankDetailRowEvent(event);
  if (!hasBlankDetailRow) return false;

  return (
    getHistoryParentGroupKey(event) === getHistoryParentGroupKey(newestEvent)
  );
};

const mergeHistoryGroupChanges = events => {
  const changes = {};

  [...events].reverse().forEach(event => {
    Object.entries(event?.changes || {}).forEach(([field, values]) => {
      if (HIDDEN_CHANGE_FIELDS.has(field)) return;

      if (!changes[field]) {
        changes[field] = {
          old: values?.old,
          new: values?.new,
        };
        return;
      }

      changes[field] = {
        old: changes[field].old,
        new: values?.new,
      };
    });
  });

  return Object.fromEntries(
    sortChangeEntries(
      Object.entries(changes).filter(([field, values]) => {
        const hasCreatedEvent = events.some(
          event => event?.action === 'created'
        );

        if (hasCreatedEvent) {
          return !isBlankChangeValue(values?.new);
        }

        return isMeaningfulChange(field, values);
      })
    )
  );
};

const mergeHistoryGroup = group => {
  if (group.length === 1) {
    return {
      ...group[0],
      history_event_ids: [group[0].id],
    };
  }

  const newestEvent = group[0];
  const createdEvent = group.find(event => event?.action === 'created');
  const actionEvent = createdEvent || newestEvent;
  const mergedChanges = mergeHistoryGroupChanges(group);

  return {
    ...newestEvent,
    action: actionEvent?.action,
    action_display: actionEvent?.action_display,
    changes: mergedChanges,
    history_group_count: group.length,
    history_event_ids: group.map(event => event.id),
    can_undo: group.every(event => event.can_undo),
    can_redo: group.every(event => event.can_redo),
    is_undone: group.every(event => event.is_undone),
    object_label: newestEvent?.object_label || actionEvent?.object_label,
    object_path: newestEvent?.object_path || actionEvent?.object_path,
  };
};

const groupChangeEventsForHistory = events => {
  const groups = [];

  events.forEach(event => {
    const currentGroup = groups[groups.length - 1];

    if (canGroupHistoryEvents(event, currentGroup)) {
      currentGroup.push(event);
      return;
    }

    groups.push([event]);
  });

  return groups.map(mergeHistoryGroup);
};

const getActorName = event =>
  event?.actor_name || event?.actor_email || 'Someone';

const getActorInitials = event => {
  if (event?.actor_initials) return event.actor_initials;

  const actorName = getActorName(event);
  return actorName
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
};

const getEventActionSummary = event => {
  const action =
    event?.action === 'created' ? 'Added' : event?.action_display || 'Changed';
  const objectType = event?.object_type_display?.toLowerCase();
  const fields = Object.keys(event?.changes || {});

  if (event?.action === 'updated' && fields.length) {
    const fieldSummary = fields.slice(0, 2).map(formatChangeField).join(', ');
    const overflow = fields.length > 2 ? ` +${fields.length - 2} more` : '';
    return `${action} ${fieldSummary}${overflow}`;
  }

  return objectType ? `${action} ${objectType}` : action;
};

const getEventFieldChanges = (event, currency) =>
  sortChangeEntries(Object.entries(event?.changes || {})).map(
    ([field, values]) => ({
      field,
      label: formatChangeField(field),
      newValue: formatChangeValue(values?.new, field, currency),
      oldValue: formatChangeValue(values?.old, field, currency),
    })
  );

const buildNavigationKey = ({ swimlanes = [], showDetailedView = {} }) => {
  const columnKey = swimlanes
    .map(column => {
      const itemId = column?.selectedItem?.id || column?.budgetId || '';
      return `${column?.type || ''}:${itemId}`;
    })
    .join('|');
  const detailKey = showDetailedView?.show
    ? `${showDetailedView?.level}:${showDetailedView?.selectedItem?.id || ''}`
    : 'closed';

  return `${columnKey}::${detailKey}`;
};

const cloneNavigationState = ({
  swimlanes = [],
  showDetailedView = {},
  swimlaneInfo = {},
  lastSwimlane = null,
}) => ({
  key: buildNavigationKey({ swimlanes, showDetailedView }),
  swimlanes: swimlanes.map(column => ({ ...column })),
  showDetailedView: { ...showDetailedView },
  swimlaneInfo: { ...swimlaneInfo },
  lastSwimlane: lastSwimlane ? { ...lastSwimlane } : null,
});

const VIEW_STATE_VERSION = 1;
const VIEW_STATE_STORAGE_PREFIX = 'CBP_BUDGET_VIEW_STATE';

const getViewStateStorageKey = budgetId =>
  `${VIEW_STATE_STORAGE_PREFIX}:${budgetId}`;

const readStoredViewState = budgetId => {
  if (typeof window === 'undefined' || !budgetId) return null;

  try {
    return JSON.parse(
      window.localStorage?.getItem(getViewStateStorageKey(budgetId)) || 'null'
    );
  } catch (error) {
    return null;
  }
};

const writeStoredViewState = (budgetId, viewState) => {
  if (typeof window === 'undefined' || !budgetId || !viewState) return;

  try {
    window.localStorage?.setItem(
      getViewStateStorageKey(budgetId),
      JSON.stringify(viewState)
    );
  } catch (error) {
    // Ignore local storage failures; backend view state is still authoritative.
  }
};

const NAVIGATION_SNAPSHOT_STORAGE_PREFIX = 'CBP_BUDGET_NAVIGATION_SNAPSHOT';

const getNavigationSnapshotStorageKey = budgetId =>
  `${NAVIGATION_SNAPSHOT_STORAGE_PREFIX}:${budgetId}`;

const readStoredNavigationSnapshot = budgetId => {
  if (typeof window === 'undefined' || !budgetId) return null;

  try {
    return JSON.parse(
      window.sessionStorage?.getItem(
        getNavigationSnapshotStorageKey(budgetId)
      ) || 'null'
    );
  } catch (error) {
    return null;
  }
};

const writeStoredNavigationSnapshot = (budgetId, snapshot) => {
  if (typeof window === 'undefined' || !budgetId || !snapshot) return;

  try {
    window.sessionStorage?.setItem(
      getNavigationSnapshotStorageKey(budgetId),
      JSON.stringify(snapshot)
    );
  } catch (error) {
    // Ignore session storage failures; persistent view state still works.
  }
};

const clearStoredNavigationSnapshot = budgetId => {
  if (typeof window === 'undefined' || !budgetId) return;

  try {
    window.sessionStorage?.removeItem(
      getNavigationSnapshotStorageKey(budgetId)
    );
  } catch (error) {
    // Ignore session storage failures; persistent view state still works.
  }
};

const getItemTypeForLevel = level => {
  if (level === 0) return 'swimlane';
  if (level === 1) return 'line-item';
  return 'line-item-children';
};

const getNextColumnTypeForLevel = level =>
  level === 0 ? 'line-item' : 'line-item-children';

const getSwimlaneReference = (level, item) =>
  level === 0 ? item?.id : item?.swimlane;

const getChildItems = (item, type) => {
  if (!item) return [];
  if (type === 'swimlane') return item?.budget_swimlane_lineitem || [];
  return item?.bs_lineitem_children || [];
};

const findItemById = (items = [], itemId) =>
  items.find(item => item?.id?.toString() === itemId?.toString());

const hasSavedViewState = viewState =>
  (Array.isArray(viewState?.hierarchy) && viewState.hierarchy.length > 0) ||
  Boolean(viewState?.panel?.open);

const getViewStateIdentityKey = viewState =>
  [
    Array.isArray(viewState?.hierarchy)
      ? viewState.hierarchy.map(item => `${item.type}:${item.id}`).join(' > ')
      : '',
    viewState?.panel?.open ? `panel:${viewState.panel.tab}` : '',
  ]
    .filter(Boolean)
    .join('|');

const isStoredNavigationSnapshotUsable = (snapshot, budgetId) => {
  if (!snapshot || snapshot.budgetId?.toString() !== budgetId?.toString()) {
    return false;
  }

  if (!Array.isArray(snapshot.swimlanes) || !snapshot.swimlanes.length) {
    return false;
  }

  if (snapshot.viewState && !hasSavedViewState(snapshot.viewState)) {
    return false;
  }

  if (
    snapshot.viewState?.budgetId &&
    snapshot.viewState.budgetId.toString() !== budgetId?.toString()
  ) {
    return false;
  }

  return true;
};

const hasViewStateProperty = tab =>
  Boolean(tab) && Object.prototype.hasOwnProperty.call(tab, 'view_state');

const getViewStateDebugEnabled = () =>
  typeof window !== 'undefined' &&
  (window.localStorage?.getItem('CBP_DEBUG_VIEW_STATE') === '1' ||
    (process.env.NODE_ENV === 'development' &&
      window.localStorage?.getItem('CBP_DEBUG_VIEW_STATE') !== '0'));

const logViewStateDebug = (...args) => {
  if (getViewStateDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.debug(
      '[CBP view state]',
      ...args.map(arg =>
        typeof arg === 'string' ? arg : JSON.stringify(arg, null, 2)
      )
    );
  }
};

const buildViewStateFromLayout = ({
  budgetId,
  swimlanes = [],
  showDetailedView = {},
  lastDetailCellState = null,
}) => {
  const hierarchy = [];

  swimlanes.forEach((column, columnIndex) => {
    if (!column?.selectedItem?.id) return;

    const level = Math.max(columnIndex - 1, 0);
    hierarchy.push({
      id: column.selectedItem.id,
      name: column.selectedItem.name,
      type: getItemTypeForLevel(level),
    });
  });

  if (showDetailedView?.show && showDetailedView?.selectedItem?.id) {
    const detailLevel = Number(showDetailedView.level || 0);
    const detailItem = showDetailedView.selectedItem;
    const alreadyTracked = hierarchy.some(
      item => item.id?.toString() === detailItem.id?.toString()
    );

    if (!alreadyTracked) {
      hierarchy.push({
        id: detailItem.id,
        name: detailItem.name,
        type: getItemTypeForLevel(detailLevel),
      });
    }
  }

  if (!hierarchy.length) return null;

  const detail =
    showDetailedView?.show && showDetailedView?.selectedItem?.id
      ? {
          itemId: showDetailedView.selectedItem.id,
          level: Number(showDetailedView.level || 0),
          type: getItemTypeForLevel(Number(showDetailedView.level || 0)),
        }
      : null;

  const cell =
    detail &&
    lastDetailCellState?.rowId &&
    lastDetailCellState?.columnKey &&
    lastDetailCellState?.detailItemId?.toString() === detail.itemId?.toString()
      ? {
          ...lastDetailCellState,
          detailItemId: detail.itemId,
          detailLevel: detail.level,
          detailSheetType: detail.type,
        }
      : null;

  return {
    version: VIEW_STATE_VERSION,
    budgetId,
    savedAt: new Date().toISOString(),
    hierarchy,
    detail,
    cell,
  };
};

const buildRestoredNavigationSnapshot = ({
  budgetData,
  budgetId,
  viewState,
}) => {
  if (!viewState || viewState.version !== VIEW_STATE_VERSION) return null;
  if (viewState.budgetId?.toString() !== budgetId?.toString()) return null;

  const hierarchy = Array.isArray(viewState.hierarchy)
    ? viewState.hierarchy
    : [];
  if (!hierarchy.length) return null;

  const selectedItems = [];
  let availableItems = budgetData?.budget_swimlane || [];

  hierarchy.forEach((savedItem, level) => {
    if (!availableItems.length) return;

    const currentItem = findItemById(availableItems, savedItem.id);
    if (!currentItem) {
      availableItems = [];
      return;
    }

    selectedItems.push(currentItem);
    availableItems = getChildItems(currentItem, getItemTypeForLevel(level));
  });

  if (!selectedItems.length) return null;

  const savedDetailId = viewState.detail?.itemId?.toString();
  const detailCandidate = selectedItems[selectedItems.length - 1];
  const detailLevel = selectedItems.length - 1;
  const detailType = getItemTypeForLevel(detailLevel);
  const detailRows = getDetailsSheet(detailCandidate, detailType);
  const canOpenDetail =
    savedDetailId &&
    detailCandidate?.id?.toString() === savedDetailId &&
    Array.isArray(detailRows);

  const visibleColumnItems = canOpenDetail
    ? selectedItems.slice(0, -1)
    : selectedItems;
  const restoredSwimlanes = [{ type: 'swimlane', budgetId }];

  visibleColumnItems.forEach((item, level) => {
    restoredSwimlanes.push({
      type: getNextColumnTypeForLevel(level),
      selectedItem: item,
      swimlane: getSwimlaneReference(level, item),
    });
  });

  return {
    key: buildNavigationKey({
      swimlanes: restoredSwimlanes,
      showDetailedView: canOpenDetail
        ? { show: true, level: detailLevel, selectedItem: detailCandidate }
        : { show: false },
    }),
    lastSwimlane: canOpenDetail
      ? {
          type: getNextColumnTypeForLevel(detailLevel),
          selectedItem: detailCandidate,
          swimlane: getSwimlaneReference(detailLevel, detailCandidate),
        }
      : null,
    restoreCellState: canOpenDetail ? viewState.cell : null,
    showDetailedView: canOpenDetail
      ? { show: true, level: detailLevel, selectedItem: detailCandidate }
      : { show: false },
    swimlaneInfo: selectedItems[0] || {},
    swimlanes: restoredSwimlanes,
  };
};

const getRestoreItemsForLevel = async ({
  budgetId,
  level,
  parentItem,
  queryClient,
}) => {
  const payload =
    level === 0
      ? { id: budgetId, type: 'swimlane' }
      : {
          id: parentItem?.id,
          type: level === 1 ? 'line-item' : 'line-item-children',
        };
  if (!payload.id) return [];

  const queryKey = getSwimlaneQueryKey(payload);
  const cachedResponse = queryClient?.getQueryData(queryKey);
  if (Array.isArray(cachedResponse?.data)) {
    return cachedResponse.data;
  }

  const queryFn = () => {
    if (payload.type === 'swimlane') {
      return API_ENDPOINTS.budgets.get_swimlanes(payload.id);
    }
    if (payload.type === 'line-item') {
      return API_ENDPOINTS.budgets.get_line_items(payload.id);
    }
    return API_ENDPOINTS.budgets.get_line_item_children(payload.id);
  };

  const response = queryClient
    ? await queryClient.fetchQuery(queryKey, queryFn, {
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
      })
    : await queryFn();

  return response?.data || [];
};

const attachRestoreChild = ({ parentItem, childItem, parentLevel }) => {
  if (!parentItem || !childItem) return;

  if (parentLevel === 0) {
    parentItem.budget_swimlane_lineitem = [childItem];
    return;
  }

  parentItem.bs_lineitem_children = [childItem];
};

const buildRestoredNavigationSnapshotFromApi = async ({
  budgetId,
  viewState,
  queryClient,
}) => {
  if (!viewState || viewState.version !== VIEW_STATE_VERSION) return null;
  if (viewState.budgetId?.toString() !== budgetId?.toString()) return null;

  const hierarchy = Array.isArray(viewState.hierarchy)
    ? viewState.hierarchy
    : [];
  if (!hierarchy.length) return null;

  let restoredRootItem = null;
  let parentItem = null;

  for (let level = 0; level < hierarchy.length; level += 1) {
    const items = await getRestoreItemsForLevel({
      budgetId,
      level,
      parentItem,
      queryClient,
    });
    const currentItem = findItemById(items, hierarchy[level].id);

    if (!currentItem) break;

    const restoredItem = { ...currentItem };
    if (!restoredRootItem) {
      restoredRootItem = restoredItem;
    }
    attachRestoreChild({
      parentItem,
      childItem: restoredItem,
      parentLevel: level - 1,
    });
    parentItem = restoredItem;
  }

  if (!restoredRootItem) return null;

  return buildRestoredNavigationSnapshot({
    budgetData: { budget_swimlane: [restoredRootItem] },
    budgetId,
    viewState,
  });
};

const buildSearchResultViewState = ({ budgetId, result }) => {
  const path = Array.isArray(result?.path) ? result.path : [];
  if (!budgetId || !path.length) return null;

  const hierarchy = path.map((item, level) => ({
    id: item.id,
    name: item.name,
    type: getItemTypeForLevel(level),
  }));
  const detailLevel = Math.max(hierarchy.length - 1, 0);
  const detailType = getItemTypeForLevel(detailLevel);
  const isDetailRowResult =
    result.type === 'line_item' || result.type === 'note';
  const shouldOpenCategoryDetail =
    isDetailRowResult || Number(result.detail_rows_count || 0) > 0;
  const detail = shouldOpenCategoryDetail
    ? {
        itemId: result.category_id || path[path.length - 1]?.id,
        level: detailLevel,
        type: detailType,
      }
    : null;
  const cell =
    isDetailRowResult && result.row_id && result.column_key
      ? {
          rowId: result.row_id,
          columnKey: result.column_key,
          detailItemId: detail?.itemId,
          detailLevel,
          detailSheetType: detailType,
          focusToken: `${Date.now()}-${result.id || result.row_id}`,
        }
      : null;

  return {
    version: VIEW_STATE_VERSION,
    budgetId,
    savedAt: new Date().toISOString(),
    hierarchy,
    detail,
    cell,
  };
};

const SwimlaneLayout = ({
  swimlaneHeight,
  budgetId,
  presenceBreadcrumbs,
  cellPresences,
  changeEvents,
  isLoadingChangeEvents,
  contextualCategorySuggestionRequest,
  onDetailInteraction,
  onSuggestionDeleted,
  onCategoryColumnItemsChange,
  mentionableUsers,
}) => {
  const {
    contextualSuggestions,
    showDetailedView,
    setShowDetailedView,
    swimlanes,
    selectedBudget,
    setSelectedBudget,
    setSwimlanes,
    openTabsList,
    openTabsListInitial,
    setOpenTabsList,
    setOpenTabsListInitial,
    budgetData,
    refetchBudgetId,
    refetchSwimlaneCosts,
    hasEditAccess,
    showAiInsights,
    setShowAiInsights,
    budgetTypes,
    setCurrentBudgetViewState,
    deepLinkViewStateByBudget,
    clearDeepLinkViewState,
  } = useHomeScreenContext();
  const queryClient = useQueryClient();
  const applyBudgetHistoryMutation =
    useBudgetMutation.useApplyBudgetHistoryMutation();
  const resetDetailViewMutation =
    useBudgetMutation.useResetDetailViewMutation();
  const moveLineItemCategoryMutation =
    useBudgetMutation.useMoveLineItemCategoryMutation();
  const moveLineItemCategoriesMutation =
    useBudgetMutation.useMoveLineItemCategoriesMutation();
  const isAicpTemplate = isAicpTemplateName(
    getBudgetTemplateName(budgetData, budgetTypes)
  );
  const swimlaneBlockRef = useRef();
  const swimlaneWrapperRef = useRef();
  const detailColumnManagerTriggerRef = useRef();
  const swimlaneDetailsWrapperRef = useRef();
  const navWidthTimerRef = useRef(null);
  const sidePanelScrollPositionRef = useRef(0);
  const wasSidePanelOpenRef = useRef(showAiInsights);
  const viewportResizeTimerRef = useRef(null);
  const [editing, setEditing] = useState({});

  const [keyHistory, setKeyHistory] = useState([]);
  const [idCat, setIdCat] = useState(0);

  const [addItemSuggestions, setAddItemSuggestions] = useState([]);
  const [lastSwimlane, setLastSwimlane] = useState(null);
  const [swimlaneInfo, setSwimlaneInfo] = useState({});
  const [swimlaneNavWidth, setSwimlaneNavWidth] = useState({ width: '100%' });
  const [isViewportResizing, setIsViewportResizing] = useState(false);
  const [newDetailViewAdded, setNewDetailViewAdded] = useState(false);
  const [showDeleteDetailConfirm, setShowDeleteDetailConfirm] = useState(false);
  const [isDeletingDetailView, setIsDeletingDetailView] = useState(false);
  const [historyActionState, setHistoryActionState] = useState(null);
  const [isDetailColumnManagerOpen, setIsDetailColumnManagerOpen] =
    useState(false);
  const [detailColumnManagerMeta, setDetailColumnManagerMeta] = useState({
    hiddenColumnCount: 0,
  });
  const [aiInsightsRefresh, setAiInsightsRefresh] = useState({
    revision: 0,
    categoryId: null,
    focusItems: [],
  });
  const aiInsightsRefreshTimerRef = useRef(null);
  const pendingAiInsightsRefreshRef = useRef({
    categoryId: null,
    full: false,
    focusItems: new Set(),
  });
  const [categoryNestHoverKey, setCategoryNestHoverKey] = useState('');
  const [categoryNestReadyKey, setCategoryNestReadyKey] = useState('');
  const [activeCategorySelectionColumn, setActiveCategorySelectionColumn] =
    useState(null);
  const isApplyingNavigationRef = useRef(false);
  const isRestoringViewStateRef = useRef(false);
  const restoredTabIdRef = useRef(null);
  const saveViewStateTimerRef = useRef(null);
  const lastSavedViewStateKeyRef = useRef('');
  const categoryNestHoverRef = useRef({
    key: '',
    ready: false,
    target: null,
    timer: null,
  });
  const categoryDragPointerRef = useRef({
    draggableId: '',
    point: null,
  });
  const [lastDetailCellState, setLastDetailCellState] = useState(null);
  const [restoreCellState, setRestoreCellState] = useState(null);
  const [navigationState, setNavigationState] = useState({
    entries: [],
    index: -1,
  });
  const deepLinkViewState = useMemo(
    () => deepLinkViewStateByBudget?.[budgetId?.toString()] || null,
    [budgetId, deepLinkViewStateByBudget]
  );
  const hasDeepLinkViewState = hasSavedViewState(deepLinkViewState);

  useEffect(() => {
    if (!deepLinkViewState?.panel?.open || !budgetId) return;
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem(
        `cinebudget-budget-side-panel-tab:${budgetId}`,
        deepLinkViewState.panel.tab || 'comments'
      );
    }
    setShowAiInsights(true);
  }, [budgetId, deepLinkViewState, setShowAiInsights]);
  const currentLayoutViewState = useMemo(
    () =>
      buildViewStateFromLayout({
        budgetId,
        lastDetailCellState,
        showDetailedView,
        swimlanes,
      }),
    [budgetId, lastDetailCellState, showDetailedView, swimlanes]
  );
  const historyMenuOptions = useMemo(() => {
    const normalizedChangeEvents = (changeEvents || [])
      .map(event => {
        if (event?.action === 'updated') {
          const changes = getMeaningfulChanges(event);
          if (!Object.keys(changes).length) return null;

          return { ...event, changes };
        }

        const changes = Object.fromEntries(
          sortChangeEntries(
            Object.entries(event?.changes || {}).filter(
              ([field, values]) =>
                !HIDDEN_CHANGE_FIELDS.has(field) &&
                !isBlankChangeValue(values?.new)
            )
          )
        );

        return { ...event, changes };
      })
      .filter(Boolean);

    const visibleChangeEvents = dedupeConsecutiveChangeEvents(
      groupChangeEventsForHistory(normalizedChangeEvents)
    );

    if (!visibleChangeEvents.length) {
      return [
        {
          value: 'empty-history',
          label: 'No changes in this session yet.',
          empty: true,
        },
      ];
    }

    return visibleChangeEvents.map(event => ({
      value: event.id,
      event,
      label: getEventActionSummary(event),
    }));
  }, [changeEvents]);

  const applyHistoryAction = useCallback(
    async ({ direction, eventIds, range = false }) => {
      if (!eventIds.length || historyActionState) return;

      setHistoryActionState({ direction, eventIds });
      try {
        const response = await applyBudgetHistoryMutation.mutateAsync({
          budget: selectedBudget?.budget || budgetId,
          direction,
          event_ids: eventIds,
        });
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: ['get-budget-change-events'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['get-swimlanes-in-budget'],
          }),
          queryClient.invalidateQueries({ queryKey: ['get-budget-data'] }),
          Promise.resolve(refetchBudgetId?.()),
          Promise.resolve(refetchSwimlaneCosts?.({ budgetId })),
        ]);
        const changedCount = response?.data?.changed_count || eventIds.length;
        const actionLabel = direction === 'undo' ? 'Undid' : 'Redid';
        toast.success(
          range || changedCount > 1
            ? `${actionLabel} ${changedCount} changes.`
            : `${actionLabel} change.`
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.detail ||
            `Unable to ${direction} this change. Please try again.`
        );
      } finally {
        setHistoryActionState(null);
      }
    },
    [
      applyBudgetHistoryMutation,
      budgetId,
      historyActionState,
      queryClient,
      refetchBudgetId,
      refetchSwimlaneCosts,
      selectedBudget?.budget,
    ]
  );

  const handleAssistantBudgetChange = useCallback(async () => {
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: ['get-swimlanes-in-budget'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['get-swimlanes-details'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['detail-sheet-totals'],
      }),
      queryClient.invalidateQueries({ queryKey: ['get-budget-data'] }),
      Promise.resolve(refetchBudgetId?.()),
      Promise.resolve(refetchSwimlaneCosts?.({ budgetId })),
    ]);
  }, [budgetId, queryClient, refetchBudgetId, refetchSwimlaneCosts]);

  const clearPendingAiInsightsRefresh = useCallback(() => {
    if (aiInsightsRefreshTimerRef.current) {
      clearTimeout(aiInsightsRefreshTimerRef.current);
      aiInsightsRefreshTimerRef.current = null;
    }
    pendingAiInsightsRefreshRef.current = {
      categoryId: null,
      full: false,
      focusItems: new Set(),
    };
  }, []);

  const scheduleAiInsightsRefresh = useCallback(
    change => {
      const categoryId = showDetailedView?.selectedItem?.id;
      if (!showAiInsights || !categoryId || !change) return;

      let pending = pendingAiInsightsRefreshRef.current;
      if (pending.categoryId?.toString() !== categoryId.toString()) {
        pending = {
          categoryId,
          full: false,
          focusItems: new Set(),
        };
        pendingAiInsightsRefreshRef.current = pending;
      }

      const focusItems = Array.isArray(change.focusItems)
        ? change.focusItems
            .map(item => String(item || '').trim())
            .filter(Boolean)
        : [];
      if (!focusItems.length) {
        pending.full = true;
        pending.focusItems.clear();
      } else if (!pending.full) {
        focusItems.forEach(item => pending.focusItems.add(item));
      }

      if (aiInsightsRefreshTimerRef.current) {
        clearTimeout(aiInsightsRefreshTimerRef.current);
      }
      aiInsightsRefreshTimerRef.current = setTimeout(() => {
        const queued = pendingAiInsightsRefreshRef.current;
        const queuedFocusItems = queued.full
          ? []
          : [...queued.focusItems].slice(0, AI_RATE_CARD_MAX_FOCUS_ITEMS);
        setAiInsightsRefresh(previous => ({
          revision: previous.revision + 1,
          categoryId: queued.categoryId,
          focusItems: queuedFocusItems,
        }));
        clearPendingAiInsightsRefresh();
      }, AI_RATE_CARD_REFRESH_DELAY_MS);
    },
    [
      clearPendingAiInsightsRefresh,
      showAiInsights,
      showDetailedView?.selectedItem?.id,
    ]
  );

  useEffect(
    () => clearPendingAiInsightsRefresh,
    [clearPendingAiInsightsRefresh, showDetailedView?.selectedItem?.id]
  );

  useEffect(() => {
    if (showAiInsights) return;
    clearPendingAiInsightsRefresh();
    setAiInsightsRefresh(previous => ({
      ...previous,
      categoryId: null,
      focusItems: [],
    }));
  }, [clearPendingAiInsightsRefresh, showAiInsights]);

  useEffect(() => {
    setIsDetailColumnManagerOpen(false);

    if (!showDetailedView?.show) {
      setDetailColumnManagerMeta({ hiddenColumnCount: 0 });
    }
  }, [showDetailedView?.selectedItem?.id, showDetailedView?.show]);

  useEffect(() => {
    if (!isDetailColumnManagerOpen) return undefined;

    const handleGlobalMouseDown = event => {
      const target = event.target;

      if (detailColumnManagerTriggerRef.current?.contains(target)) return;
      if (target?.closest?.('.detail-column-manager')) return;

      setIsDetailColumnManagerOpen(false);
    };

    const handleGlobalKeyDown = event => {
      if (event.key === 'Escape') {
        setIsDetailColumnManagerOpen(false);
      }
    };

    window.addEventListener('mousedown', handleGlobalMouseDown);
    window.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      window.removeEventListener('mousedown', handleGlobalMouseDown);
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isDetailColumnManagerOpen]);

  const refs = useRef([]);

  const clearCategoryNestHover = useCallback(() => {
    if (categoryNestHoverRef.current.timer) {
      clearTimeout(categoryNestHoverRef.current.timer);
    }

    categoryNestHoverRef.current = {
      key: '',
      ready: false,
      target: null,
      timer: null,
    };
    setCategoryNestHoverKey('');
    setCategoryNestReadyKey('');
  }, []);

  const armCategoryNestHover = useCallback(
    target => {
      const targetKey = getCategoryNestTargetKey(target);

      if (!targetKey) {
        clearCategoryNestHover();
        return;
      }

      if (categoryNestHoverRef.current.key === targetKey) return;

      clearCategoryNestHover();
      const timer = setTimeout(() => {
        if (categoryNestHoverRef.current.key !== targetKey) return;

        categoryNestHoverRef.current = {
          ...categoryNestHoverRef.current,
          ready: true,
          timer: null,
        };
        setCategoryNestReadyKey(targetKey);
      }, CATEGORY_NEST_HOLD_MS);

      categoryNestHoverRef.current = {
        key: targetKey,
        ready: false,
        target,
        timer,
      };
      setCategoryNestHoverKey(targetKey);
      setCategoryNestReadyKey('');
    },
    [clearCategoryNestHover]
  );

  const updateCategoryNestHoverFromPoint = useCallback(
    point => {
      categoryDragPointerRef.current = {
        ...categoryDragPointerRef.current,
        point,
      };

      const target = getCategoryNestTargetFromPoint({
        point,
        draggedDraggableId: categoryDragPointerRef.current.draggableId,
      });

      armCategoryNestHover(target);
    },
    [armCategoryNestHover]
  );

  const handleCategoryDragPointerMove = useCallback(
    event => {
      if (!categoryDragPointerRef.current.draggableId) return;

      const point = getDragPointerClientPoint(event);
      if (!point) return;

      updateCategoryNestHoverFromPoint(point);
    },
    [updateCategoryNestHoverFromPoint]
  );

  const stopCategoryDragPointerTracking = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('mousemove', handleCategoryDragPointerMove);
      window.removeEventListener('touchmove', handleCategoryDragPointerMove);
    }

    categoryDragPointerRef.current = {
      draggableId: '',
      point: null,
    };
  }, [handleCategoryDragPointerMove]);

  const startCategoryDragPointerTracking = useCallback(
    start => {
      stopCategoryDragPointerTracking();
      categoryDragPointerRef.current = {
        draggableId: start?.draggableId || '',
        point: null,
      };

      if (typeof window !== 'undefined') {
        window.addEventListener('mousemove', handleCategoryDragPointerMove);
        window.addEventListener('touchmove', handleCategoryDragPointerMove, {
          passive: true,
        });
      }
    },
    [handleCategoryDragPointerMove, stopCategoryDragPointerTracking]
  );

  useEffect(
    () => () => {
      clearCategoryNestHover();
      stopCategoryDragPointerTracking();
    },
    [clearCategoryNestHover, stopCategoryDragPointerTracking]
  );

  const updateOpenTabViewStateLocally = useCallback(
    (tabId, viewState) => {
      const patchTab = tab =>
        tab?.id?.toString() === tabId?.toString()
          ? { ...tab, view_state: viewState }
          : tab;

      setSelectedBudget(current =>
        current?.id?.toString() === tabId?.toString()
          ? { ...current, view_state: viewState }
          : current
      );
      setOpenTabsList(current =>
        Array.isArray(current) ? current.map(patchTab) : current
      );
      setOpenTabsListInitial(current =>
        Array.isArray(current) ? current.map(patchTab) : current
      );
    },
    [setOpenTabsList, setOpenTabsListInitial, setSelectedBudget]
  );

  useEffect(() => {
    setCurrentBudgetViewState(currentLayoutViewState);

    return () => {
      setCurrentBudgetViewState(current =>
        current?.budgetId?.toString() === budgetId?.toString() ? null : current
      );
    };
  }, [budgetId, currentLayoutViewState, setCurrentBudgetViewState]);

  const selectedTabViewStateCandidates = useMemo(() => {
    if (hasDeepLinkViewState) {
      return [deepLinkViewState];
    }

    const locallyStoredViewState = readStoredViewState(budgetId);
    const candidateTabs = [
      ...(Array.isArray(openTabsList) ? openTabsList : []),
      ...(Array.isArray(openTabsListInitial) ? openTabsListInitial : []),
      selectedBudget,
    ].filter(Boolean);

    const matchingTabs = candidateTabs.filter(
      tab =>
        tab?.id?.toString() === selectedBudget?.id?.toString() ||
        tab?.budget?.toString() === budgetId?.toString()
    );

    return [
      ...matchingTabs.map(tab => tab?.view_state).filter(hasSavedViewState),
      selectedBudget?.view_state,
      locallyStoredViewState,
    ].filter(hasSavedViewState);
  }, [
    budgetId,
    deepLinkViewState,
    hasDeepLinkViewState,
    openTabsList,
    openTabsListInitial,
    selectedBudget,
  ]);

  const selectedTabCachedNavigationSnapshot = useMemo(() => {
    if (hasDeepLinkViewState) return null;

    const cachedSnapshot = readStoredNavigationSnapshot(budgetId);
    if (!isStoredNavigationSnapshotUsable(cachedSnapshot, budgetId)) {
      return null;
    }

    const cachedViewStateKey = getViewStateIdentityKey(
      cachedSnapshot.viewState
    );

    if (!cachedViewStateKey || !selectedTabViewStateCandidates.length) {
      return cachedSnapshot;
    }

    const hasMatchingViewState = selectedTabViewStateCandidates.some(
      candidate => getViewStateIdentityKey(candidate) === cachedViewStateKey
    );

    return hasMatchingViewState ? cachedSnapshot : null;
  }, [budgetId, hasDeepLinkViewState, selectedTabViewStateCandidates]);

  const selectedTabHasLoadedViewState = useMemo(() => {
    const candidateTabs = [
      ...(Array.isArray(openTabsList) ? openTabsList : []),
      ...(Array.isArray(openTabsListInitial) ? openTabsListInitial : []),
      selectedBudget,
    ].filter(Boolean);

    return candidateTabs
      .filter(
        tab =>
          tab?.id?.toString() === selectedBudget?.id?.toString() ||
          tab?.budget?.toString() === budgetId?.toString()
      )
      .some(hasViewStateProperty);
  }, [budgetId, openTabsList, openTabsListInitial, selectedBudget]);

  const handleDetailCellFocus = useCallback(
    cellState => {
      if (!cellState?.rowId || !cellState?.columnKey) return;
      if (!showDetailedView?.show || !showDetailedView?.selectedItem?.id) {
        return;
      }

      setLastDetailCellState({
        ...cellState,
        detailItemId: showDetailedView.selectedItem.id,
        detailLevel: showDetailedView.level,
        detailSheetType: getItemTypeForLevel(
          Number(showDetailedView.level || 0)
        ),
      });
    },
    [showDetailedView]
  );

  useEffect(() => {
    if (isApplyingNavigationRef.current) return;

    const snapshot = cloneNavigationState({
      swimlanes,
      showDetailedView,
      swimlaneInfo,
      lastSwimlane,
    });

    setNavigationState(current => {
      const currentSnapshot = current.entries[current.index];
      if (currentSnapshot?.key === snapshot.key) return current;

      const entries = [
        ...current.entries.slice(0, current.index + 1),
        snapshot,
      ].slice(-50);

      return {
        entries,
        index: entries.length - 1,
      };
    });
  }, [lastSwimlane, showDetailedView, swimlaneInfo, swimlanes]);

  const applyNavigationSnapshot = useCallback(
    snapshot => {
      if (!snapshot) return;

      isApplyingNavigationRef.current = true;
      setSwimlaneInfo(snapshot.swimlaneInfo || {});
      setLastSwimlane(snapshot.lastSwimlane || null);
      setSwimlanes(snapshot.swimlanes || [{ type: 'swimlane', budgetId }]);
      setShowDetailedView(snapshot.showDetailedView || { show: false });

      setTimeout(() => {
        isApplyingNavigationRef.current = false;
      }, 0);
    },
    [budgetId, setShowDetailedView, setSwimlanes]
  );

  useEffect(() => {
    isApplyingNavigationRef.current = true;
    isRestoringViewStateRef.current = false;
    restoredTabIdRef.current = null;
    setRestoreCellState(null);
    setLastDetailCellState(null);
    setActiveCategorySelectionColumn(null);
    lastSavedViewStateKeyRef.current = '';

    setTimeout(() => {
      isApplyingNavigationRef.current = false;
    }, 0);
  }, [budgetId, selectedBudget?.id, setSwimlanes]);

  useEffect(() => {
    if (!selectedBudget?.id || !budgetId) return undefined;
    if (selectedBudget?.budget?.toString() !== budgetId?.toString()) {
      return undefined;
    }
    if (!budgetData && !selectedTabCachedNavigationSnapshot) {
      return undefined;
    }
    if (budgetData && budgetData?.id?.toString() !== budgetId?.toString()) {
      return undefined;
    }
    if (restoredTabIdRef.current === selectedBudget.id) return undefined;

    let cancelled = false;
    const hasViewState = selectedTabViewStateCandidates.length > 0;
    const candidateKeys = selectedTabViewStateCandidates.map(
      getViewStateIdentityKey
    );
    const isSnapshotCompleteForRequest = snapshot => {
      if (!hasDeepLinkViewState) return Boolean(snapshot);
      if (!snapshot) return false;

      if (deepLinkViewState?.detail) {
        return Boolean(snapshot.showDetailedView?.show);
      }

      const expectedColumnCount =
        (deepLinkViewState?.hierarchy?.length || 0) + 1;
      return (snapshot.swimlanes?.length || 0) >= expectedColumnCount;
    };

    const applyRestoredSnapshot = ({ snapshot, source, viewState }) => {
      if (cancelled) return;

      const viewStateKey = JSON.stringify(viewState || {});
      restoredTabIdRef.current = selectedBudget.id;
      lastSavedViewStateKeyRef.current = viewStateKey;
      setRestoreCellState(snapshot.restoreCellState || null);
      isRestoringViewStateRef.current = false;
      logViewStateDebug('restore applied', {
        budgetId,
        tabId: selectedBudget.id,
        source,
        swimlanes: snapshot.swimlanes?.map(column => ({
          type: column?.type,
          selectedItemId: column?.selectedItem?.id,
          selectedItemName: column?.selectedItem?.name,
        })),
        detail: snapshot.showDetailedView?.show
          ? {
              id: snapshot.showDetailedView?.selectedItem?.id,
              name: snapshot.showDetailedView?.selectedItem?.name,
              level: snapshot.showDetailedView?.level,
            }
          : null,
        restoreCellState: snapshot.restoreCellState,
      });
      applyNavigationSnapshot(snapshot);
      if (hasDeepLinkViewState) {
        clearDeepLinkViewState(budgetId);
      }
    };

    const defaultToRootView = viewState => {
      if (cancelled) return;

      logViewStateDebug('restore defaulted', {
        budgetId,
        tabId: selectedBudget.id,
        hadSavedState: hasViewState,
      });
      if (hasViewState) {
        lastSavedViewStateKeyRef.current = JSON.stringify(viewState || {});
      }
      restoredTabIdRef.current = selectedBudget.id;
      setRestoreCellState(null);
      isRestoringViewStateRef.current = false;
      applyNavigationSnapshot({
        key: buildNavigationKey({
          swimlanes: [{ type: 'swimlane', budgetId }],
          showDetailedView: { show: false },
        }),
        lastSwimlane: null,
        restoreCellState: null,
        showDetailedView: { show: false },
        swimlaneInfo: {},
        swimlanes: [{ type: 'swimlane', budgetId }],
      });
      if (hasDeepLinkViewState) {
        clearDeepLinkViewState(budgetId);
      }
    };

    const restoreViewState = async () => {
      logViewStateDebug('restore attempt', {
        budgetId,
        tabId: selectedBudget.id,
        hasViewState,
        candidates: selectedTabViewStateCandidates.length,
        selectedBudgetHasViewState: hasSavedViewState(
          selectedBudget?.view_state
        ),
        candidateKeys,
        budgetDataId: budgetData?.id,
        hasCachedNavigationSnapshot: Boolean(
          selectedTabCachedNavigationSnapshot
        ),
        selectedTabHasLoadedViewState,
      });

      if (selectedTabCachedNavigationSnapshot) {
        isRestoringViewStateRef.current = true;
        applyRestoredSnapshot({
          snapshot: selectedTabCachedNavigationSnapshot,
          source: 'cached-navigation',
          viewState:
            selectedTabCachedNavigationSnapshot.viewState ||
            selectedTabViewStateCandidates[0],
        });
        return;
      }

      if (!hasViewState && !selectedTabHasLoadedViewState) {
        logViewStateDebug('restore waiting for tab payload', {
          budgetId,
          tabId: selectedBudget.id,
        });
        return;
      }

      if (!budgetData) {
        return;
      }

      if (hasViewState) {
        isRestoringViewStateRef.current = true;

        for (const viewState of selectedTabViewStateCandidates) {
          try {
            const apiSnapshot = await buildRestoredNavigationSnapshotFromApi({
              budgetId,
              queryClient,
              viewState,
            });

            if (isSnapshotCompleteForRequest(apiSnapshot)) {
              applyRestoredSnapshot({
                snapshot: apiSnapshot,
                source: 'api',
                viewState,
              });
              return;
            }
          } catch (error) {
            logViewStateDebug('restore api failed', {
              budgetId,
              tabId: selectedBudget.id,
              error: error?.message || String(error),
            });
          }
        }

        const localRestoreCandidate =
          selectedTabViewStateCandidates
            .map(viewState => ({
              viewState,
              snapshot: buildRestoredNavigationSnapshot({
                budgetData,
                budgetId,
                viewState,
              }),
            }))
            .find(candidate => candidate.snapshot) || null;

        if (localRestoreCandidate?.snapshot) {
          if (isSnapshotCompleteForRequest(localRestoreCandidate.snapshot)) {
            applyRestoredSnapshot({
              snapshot: localRestoreCandidate.snapshot,
              source: 'loaded-data',
              viewState: localRestoreCandidate.viewState,
            });
            return;
          }
        }

        defaultToRootView(selectedTabViewStateCandidates[0]);
        return;
      }

      defaultToRootView({});
    };

    restoreViewState();

    return () => {
      cancelled = true;
      isRestoringViewStateRef.current = false;
    };
  }, [
    applyNavigationSnapshot,
    budgetData,
    budgetId,
    clearDeepLinkViewState,
    deepLinkViewState,
    hasDeepLinkViewState,
    queryClient,
    selectedBudget?.budget,
    selectedBudget?.id,
    selectedBudget?.view_state,
    selectedTabCachedNavigationSnapshot,
    selectedTabHasLoadedViewState,
    selectedTabViewStateCandidates,
  ]);

  useEffect(() => {
    if (!selectedBudget?.id || !selectedBudget?.budget) return undefined;
    if (selectedBudget.budget?.toString() !== budgetId?.toString()) {
      return undefined;
    }
    if (isApplyingNavigationRef.current) return undefined;
    if (isRestoringViewStateRef.current) return undefined;
    if (restoredTabIdRef.current !== selectedBudget.id) return undefined;

    const nextViewState = currentLayoutViewState;

    if (!nextViewState) return undefined;

    writeStoredNavigationSnapshot(budgetId, {
      ...cloneNavigationState({
        swimlanes,
        showDetailedView,
        swimlaneInfo,
        lastSwimlane,
      }),
      budgetId,
      restoreCellState: nextViewState.cell,
      savedAt: nextViewState.savedAt,
      viewState: nextViewState,
    });

    const viewStateKey = JSON.stringify(nextViewState);
    if (viewStateKey === lastSavedViewStateKeyRef.current) {
      return undefined;
    }

    lastSavedViewStateKeyRef.current = viewStateKey;
    writeStoredViewState(budgetId, nextViewState);
    updateOpenTabViewStateLocally(selectedBudget.id, nextViewState);
    logViewStateDebug('save queued', {
      budgetId,
      tabId: selectedBudget.id,
      hierarchy: nextViewState.hierarchy,
      detail: nextViewState.detail,
      cell: nextViewState.cell,
    });

    if (saveViewStateTimerRef.current) {
      clearTimeout(saveViewStateTimerRef.current);
    }

    saveViewStateTimerRef.current = setTimeout(() => {
      API_ENDPOINTS.tabs
        .update_view_state({
          tabId: selectedBudget.id,
          view_state: nextViewState,
        })
        .then(() => {
          logViewStateDebug('save confirmed', {
            budgetId,
            tabId: selectedBudget.id,
            hierarchy: nextViewState.hierarchy,
            detail: nextViewState.detail,
            cell: nextViewState.cell,
          });
        })
        .catch(error => {
          console.error('Failed to save budget view state', error);
        });
    }, 500);

    return undefined;
  }, [
    budgetId,
    currentLayoutViewState,
    lastSwimlane,
    lastDetailCellState,
    selectedBudget?.budget,
    selectedBudget?.id,
    showDetailedView,
    swimlaneInfo,
    swimlanes,
    updateOpenTabViewStateLocally,
  ]);

  const canNavigateBack = navigationState.index > 0;
  const canNavigateForward =
    navigationState.index >= 0 &&
    navigationState.index < navigationState.entries.length - 1;

  const handleNavigateBack = () => {
    if (!canNavigateBack) return;

    const nextIndex = navigationState.index - 1;
    const snapshot = navigationState.entries[nextIndex];
    setNavigationState(current => ({
      ...current,
      index: nextIndex,
    }));
    applyNavigationSnapshot(snapshot);
  };

  const handleNavigateForward = () => {
    if (!canNavigateForward) return;

    const nextIndex = navigationState.index + 1;
    const snapshot = navigationState.entries[nextIndex];
    setNavigationState(current => ({
      ...current,
      index: nextIndex,
    }));
    applyNavigationSnapshot(snapshot);
  };

  const handleBudgetSearchResultSelect = useCallback(
    async result => {
      const viewState = buildSearchResultViewState({ budgetId, result });
      if (!viewState) return;

      try {
        const snapshot = await buildRestoredNavigationSnapshotFromApi({
          budgetId,
          queryClient,
          viewState,
        });

        if (!snapshot) return;

        const nextCellState = snapshot.restoreCellState || null;
        setRestoreCellState(nextCellState);
        setLastDetailCellState(nextCellState);
        applyNavigationSnapshot(snapshot);
      } catch (error) {
        toast.error(
          'Could not open that search result. Please refresh and try again.',
          toastStyle
        );
      }
    },
    [applyNavigationSnapshot, budgetId, queryClient]
  );

  const measureNavWidth = useCallback(() => {
    const swimlaneWidth = swimlaneWrapperRef.current?.clientWidth || 0;
    const detailsWidth = swimlaneDetailsWrapperRef?.current?.clientWidth || 0;

    const nextWidth = {
      swimlaneWidth: detailsWidth ? swimlaneWidth : '100%',
      detailsWidth,
      width: swimlaneWidth + detailsWidth,
    };

    setSwimlaneNavWidth(current =>
      current.swimlaneWidth === nextWidth.swimlaneWidth &&
      current.detailsWidth === nextWidth.detailsWidth &&
      current.width === nextWidth.width
        ? current
        : nextWidth
    );
  }, []);

  const setSwimlaneHorizontalScroll = useCallback(scrollLeft => {
    const swimlaneBlock = swimlaneBlockRef.current;
    if (!swimlaneBlock) return;

    const previousScrollBehavior = swimlaneBlock.style.scrollBehavior;
    swimlaneBlock.style.scrollBehavior = 'auto';
    swimlaneBlock.scrollLeft = Math.max(0, scrollLeft);
    swimlaneBlock.style.scrollBehavior = previousScrollBehavior;
  }, []);

  const revealSidePanel = useCallback(() => {
    const swimlaneBlock = swimlaneBlockRef.current;
    if (!swimlaneBlock) return;

    setSwimlaneHorizontalScroll(
      swimlaneBlock.scrollWidth - swimlaneBlock.clientWidth
    );
  }, [setSwimlaneHorizontalScroll]);

  const setNavWidth = useCallback(() => {
    if (navWidthTimerRef.current) {
      clearTimeout(navWidthTimerRef.current);
    }
    navWidthTimerRef.current = setTimeout(() => {
      navWidthTimerRef.current = null;
      measureNavWidth();
    }, NAV_WIDTH_UPDATE_DELAY_MS);
  }, [measureNavWidth]);

  useLayoutEffect(() => {
    if (navWidthTimerRef.current) {
      clearTimeout(navWidthTimerRef.current);
      navWidthTimerRef.current = null;
    }
    measureNavWidth();
  }, [measureNavWidth, showAiInsights, showDetailedView, swimlanes]);

  useLayoutEffect(() => {
    const wasSidePanelOpen = wasSidePanelOpenRef.current;

    if (showAiInsights) {
      if (!wasSidePanelOpen) {
        sidePanelScrollPositionRef.current =
          swimlaneBlockRef.current?.scrollLeft || 0;
      }
      revealSidePanel();
    } else if (wasSidePanelOpen) {
      setSwimlaneHorizontalScroll(sidePanelScrollPositionRef.current);
    }

    wasSidePanelOpenRef.current = showAiInsights;
  }, [
    revealSidePanel,
    setSwimlaneHorizontalScroll,
    showAiInsights,
    showDetailedView?.selectedItem?.id,
    showDetailedView?.show,
    swimlanes.length,
  ]);

  useEffect(() => {
    setNavWidth();
  }, [detailColumnManagerMeta.hiddenColumnCount, setNavWidth]);

  useEffect(() => {
    const handleViewportResize = () => {
      setIsViewportResizing(true);
      setNavWidth();
      if (viewportResizeTimerRef.current) {
        clearTimeout(viewportResizeTimerRef.current);
      }
      viewportResizeTimerRef.current = setTimeout(() => {
        viewportResizeTimerRef.current = null;
        setIsViewportResizing(false);
      }, VIEWPORT_RESIZE_SETTLE_MS);
    };
    window.addEventListener('resize', handleViewportResize);

    return () => {
      window.removeEventListener('resize', handleViewportResize);
      if (viewportResizeTimerRef.current) {
        clearTimeout(viewportResizeTimerRef.current);
      }
      if (navWidthTimerRef.current) {
        clearTimeout(navWidthTimerRef.current);
      }
    };
  }, [setNavWidth]);

  useEffect(() => {
    setAddItemSuggestions(
      prepareDropDownOptions(contextualSuggestions, 'name', 'id')
    );
  }, [contextualSuggestions]);

  // Reset layout state after a top-level swimlane (category) is deleted
  const handleTopLevelSwimlaneDeleted = useCallback(() => {
    // Clear the nav label
    setSwimlaneInfo({});
    // Reset columns to just the root swimlane for this budget
    setSwimlanes([{ type: 'swimlane', budgetId }]);
    // Ensure detailed view is closed
    setShowDetailedView({ show: false });
  }, [budgetId, setShowDetailedView, setSwimlanes]);

  const addDetailedView = (level, selectedItem) => () => {
    setLastSwimlane(swimlanes[swimlanes.length - 1] || null);
    setSwimlanes(swimlanes.length > 1 ? swimlanes.slice(0, -1) : swimlanes);
    setNewDetailViewAdded(true);
    setShowDetailedView({ show: true, level, selectedItem });
  };

  const addSwimLanes = async (level, selectedItem, swimlane, hasDetails) => {
    if (level === 0) {
      setSwimlaneInfo(selectedItem);
    }
    const type = level === 0 ? 'line-item' : 'line-item-children';
    const currentSwimlanes = swimlanes.slice(0, level + 1) || [];

    if (hasDetails) {
      setSwimlanes([...currentSwimlanes]);
      setLastSwimlane({ type, selectedItem, swimlane });
      setShowDetailedView({ show: true, level, selectedItem });
    } else {
      setSwimlanes([...currentSwimlanes, { type, selectedItem, swimlane }]);
      setShowDetailedView({ show: false });
    }
  };

  const closeDetailedView = () => {
    const currentDetailId = showDetailedView?.selectedItem?.id?.toString();
    const lastSwimlaneId = lastSwimlane?.selectedItem?.id?.toString();

    if (lastSwimlane && currentDetailId === lastSwimlaneId) {
      setSwimlanes([...swimlanes, lastSwimlane]);
    }
    setShowDetailedView({ show: false });
  };

  const getEmptyChildColumn = useCallback((level, selectedItem) => {
    return {
      type: level === 0 ? 'line-item' : 'line-item-children',
      selectedItem,
      swimlane: level === 0 ? selectedItem?.id : selectedItem?.swimlane,
    };
  }, []);

  const getResetSelectedItem = useCallback(
    (selectedItem, responseData, level) => {
      const resetItem = {
        ...selectedItem,
        ...(responseData || {}),
        cost: responseData?.cost ?? 0,
      };

      if (level === 0) {
        return {
          ...resetItem,
          swimlane_detail_sheets: [],
          budget_swimlane_lineitem: [],
        };
      }

      return {
        ...resetItem,
        swimlane_item_detail_sheets: [],
        bs_lineitem_children: [],
      };
    },
    []
  );

  const restoreResetDetailColumn = useCallback(
    (level, resetItem) => {
      const nextColumn = getEmptyChildColumn(level, resetItem);

      if (level === 0) {
        setSwimlaneInfo(resetItem);
      }

      setLastSwimlane(null);
      setSwimlanes(currentSwimlanes => {
        const rootColumn = { type: 'swimlane', budgetId };
        const baseColumns =
          Array.isArray(currentSwimlanes) && currentSwimlanes.length
            ? currentSwimlanes.slice(0, level + 1)
            : [rootColumn];

        return [...baseColumns, nextColumn];
      });
      setShowDetailedView({ show: false });
    },
    [budgetId, getEmptyChildColumn, setShowDetailedView, setSwimlanes]
  );

  const closeMovedCategoryDescendants = useCallback(
    movedItem => {
      const movedItemId = movedItem?.id;

      if (
        movedItemId === null ||
        movedItemId === undefined ||
        movedItemId === ''
      ) {
        return;
      }

      const openColumnIndex = getOpenColumnIndexForCategory(
        swimlanes,
        movedItemId
      );
      const isMovedDetailOpen =
        showDetailedView?.show &&
        idsMatch(showDetailedView?.selectedItem?.id, movedItemId);
      const isMovedLastSwimlane = idsMatch(
        lastSwimlane?.selectedItem?.id,
        movedItemId
      );

      if (
        openColumnIndex === -1 &&
        !isMovedDetailOpen &&
        !isMovedLastSwimlane
      ) {
        return;
      }

      if (openColumnIndex === -1) {
        setLastSwimlane(null);
        setShowDetailedView({ show: false });
        return;
      }

      setLastSwimlane(null);
      setShowDetailedView({ show: false });

      if (openColumnIndex <= 1) {
        setSwimlaneInfo({});
      }

      setSwimlanes(currentSwimlanes =>
        currentSwimlanes.length > openColumnIndex
          ? currentSwimlanes.slice(0, openColumnIndex)
          : currentSwimlanes
      );
    },
    [
      lastSwimlane?.selectedItem?.id,
      setShowDetailedView,
      setSwimlanes,
      showDetailedView?.selectedItem?.id,
      showDetailedView?.show,
      swimlanes,
    ]
  );

  const getDetailViewDeleteType = useCallback(level => {
    if (level === 0) {
      return 'swimlane';
    }

    if (level === 1) {
      return 'line-item';
    }

    return 'line-item-children';
  }, []);

  const handleDetailMenuAction = action => {
    if (action === 'close-detail-view') {
      closeDetailedView();
      return;
    }

    if (action === 'delete-detail-view') {
      setShowDeleteDetailConfirm(true);
    }
  };

  const renderHistoryMenuItem = (menu, menuIndex) => {
    if (menu.empty) {
      return (
        <div className="session-history-dropdown__empty">
          {isLoadingChangeEvents
            ? 'Checking for session changes...'
            : menu.label}
        </div>
      );
    }

    const event = menu.event;
    const eventCurrency =
      budgetData?.primary_currency || selectedBudget?.primary_currency;
    const fieldChanges = getEventFieldChanges(event, eventCurrency);
    const eventIds = getHistoryEventIds(event);
    const undoThroughEventIds = getUndoThroughEventIds(
      historyMenuOptions,
      menuIndex
    );
    const redoThroughEventIds = getRedoThroughEventIds(
      historyMenuOptions,
      menuIndex
    );
    const canEditHistory = hasEditAccess?.();
    const isApplyingHistory = Boolean(historyActionState);
    const handleHistoryButtonClick =
      (direction, selectedEventIds, range) => clickEvent => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        applyHistoryAction({
          direction,
          eventIds: selectedEventIds,
          range,
        });
      };

    return (
      <div
        className={`session-history-dropdown__item ${
          event?.is_undone ? 'session-history-dropdown__item--undone' : ''
        }`}
      >
        <span
          className="session-history-dropdown__avatar"
          style={{ backgroundColor: event?.actor_color || '#4285f4' }}
        >
          {getActorInitials(event)}
        </span>
        <span className="session-history-dropdown__content">
          <span className="session-history-dropdown__line">
            <strong>{getActorName(event)}</strong>{' '}
            {getEventActionSummary(event).toLowerCase()}
          </span>
          <span className="session-history-dropdown__target">
            {event?.object_path ||
              event?.object_label ||
              event?.object_type_display ||
              'Budget'}
          </span>
          {Boolean(fieldChanges.length) && (
            <span className="session-history-dropdown__fields">
              {fieldChanges.map(change => (
                <span
                  className="session-history-dropdown__field-change"
                  key={change.field}
                >
                  <span className="session-history-dropdown__field-label">
                    {change.label}:
                  </span>
                  {event?.action === 'created' ? (
                    <span className="session-history-dropdown__field-value">
                      {change.newValue}
                    </span>
                  ) : (
                    <>
                      <span className="session-history-dropdown__field-value session-history-dropdown__field-value--old">
                        {change.oldValue}
                      </span>
                      <span className="session-history-dropdown__field-arrow">
                        →
                      </span>
                      <span className="session-history-dropdown__field-value">
                        {change.newValue}
                      </span>
                    </>
                  )}
                </span>
              ))}
            </span>
          )}
          <span className="session-history-dropdown__time">
            {formatDateTime(event?.created_on, 'h:mm:ss A')}
            {event?.is_undone && (
              <span className="session-history-dropdown__status">Undone</span>
            )}
          </span>
          {canEditHistory && (event?.can_undo || event?.can_redo) && (
            <span className="session-history-dropdown__actions">
              {event?.can_undo && (
                <button
                  className="session-history-dropdown__action"
                  disabled={isApplyingHistory}
                  onClick={handleHistoryButtonClick('undo', eventIds, false)}
                  type="button"
                >
                  Undo
                </button>
              )}
              {event?.can_redo && (
                <button
                  className="session-history-dropdown__action"
                  disabled={isApplyingHistory}
                  onClick={handleHistoryButtonClick('redo', eventIds, false)}
                  type="button"
                >
                  Redo
                </button>
              )}
              {event?.can_undo &&
                undoThroughEventIds.length > eventIds.length && (
                  <button
                    className="session-history-dropdown__action session-history-dropdown__action--range"
                    disabled={isApplyingHistory}
                    onClick={handleHistoryButtonClick(
                      'undo',
                      undoThroughEventIds,
                      true
                    )}
                    type="button"
                  >
                    Undo to here
                  </button>
                )}
              {event?.can_redo &&
                redoThroughEventIds.length > eventIds.length && (
                  <button
                    className="session-history-dropdown__action session-history-dropdown__action--range"
                    disabled={isApplyingHistory}
                    onClick={handleHistoryButtonClick(
                      'redo',
                      redoThroughEventIds,
                      true
                    )}
                    type="button"
                  >
                    Redo to here
                  </button>
                )}
            </span>
          )}
        </span>
      </div>
    );
  };

  const hideDeleteDetailConfirm = () => {
    if (!isDeletingDetailView) {
      setShowDeleteDetailConfirm(false);
    }
  };

  const handleDeleteDetailView = async () => {
    const selectedItem = showDetailedView?.selectedItem;

    if (!selectedItem?.id) {
      setShowDeleteDetailConfirm(false);
      return;
    }

    const payload = {
      type: getDetailViewDeleteType(showDetailedView?.level),
      id: selectedItem.id,
    };

    setIsDeletingDetailView(true);

    try {
      const response = await resetDetailViewMutation.mutateAsync(payload);
      const resetItem = getResetSelectedItem(
        selectedItem,
        response?.data,
        showDetailedView?.level
      );
      setShowDeleteDetailConfirm(false);
      refetchBudgetId?.();
      refetchSwimlaneCosts?.({ budgetId });
      restoreResetDetailColumn(showDetailedView?.level, resetItem);
    } catch (error) {
      console.error('Failed to reset detail view', error);
    } finally {
      setIsDeletingDetailView(false);
    }
  };

  const handleBackClick = () => {
    swimlaneBlockRef?.current?.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
  };

  const renderDetailSessionActions = () => (
    <div className="detail-view-session-actions">
      <button
        type="button"
        className="detail-view-session-button"
        disabled={!canNavigateBack}
        onClick={handleNavigateBack}
        title="Back"
        aria-label="Back"
      >
        <Icon name="ArrowBackIcon" />
      </button>
      <button
        type="button"
        className="detail-view-session-button"
        disabled={!canNavigateForward}
        onClick={handleNavigateForward}
        title="Forward"
        aria-label="Forward"
      >
        <Icon name="ArrowForwardIcon" />
      </button>
      <DropDownMenu
        className="session-history-dropdown"
        toggleClass="session-history-dropdown__toggle"
        menuClass="session-history-dropdown__menu"
        toggleIcon={
          <Icon name="HistoryIcon" className="session-history-dropdown__icon" />
        }
        menuOptions={historyMenuOptions}
        onChange={() => null}
        renderMenuItemContent={renderHistoryMenuItem}
        suppressMenuItemTitle
      />
    </div>
  );

  const renderSwimlaneNav = () => {
    const detailHeaderWidth = swimlaneNavWidth?.width || '100%';
    const selectedCategoryPath = swimlanes
      .slice(1)
      .map(column => column?.selectedItem)
      .filter(item => item?.name);
    const headerBreadcrumbItems = showDetailedView?.show
      ? selectedCategoryPath.filter(
          item => !idsMatch(item?.id, showDetailedView?.selectedItem?.id)
        )
      : selectedCategoryPath;
    const categoryBreadcrumbTitle = [
      ...headerBreadcrumbItems.map(item => item.name),
      showDetailedView?.selectedItem?.name,
    ]
      .filter(Boolean)
      .join(' > ');

    return (
      <div
        className={`budget-swimlane-nav-block${
          showDetailedView?.show || showAiInsights
            ? ' budget-swimlane-nav-block--with-rate-card'
            : ''
        }`}
        style={{ width: detailHeaderWidth }}
      >
        {renderDetailSessionActions()}
        <div
          className={`budget-swimlane-nav${
            showDetailedView?.show ? '' : ' budget-swimlane-nav--without-detail'
          }`}
          style={{ width: swimlaneNavWidth?.swimlaneWidth }}
        >
          {headerBreadcrumbItems.length > 0 ? (
            <div
              className="swimlane-name detail-view-breadcrumb-ancestors"
              onClick={handleBackClick}
              title={categoryBreadcrumbTitle}
              aria-label={`Category path: ${categoryBreadcrumbTitle}`}
            >
              <span className="detail-view-breadcrumb-track">
                {headerBreadcrumbItems.map((item, index) => (
                  <React.Fragment key={item.id || item.name}>
                    <span className="detail-view-breadcrumb-crumb">
                      {item.name}
                    </span>
                    {(showDetailedView?.show ||
                      index < headerBreadcrumbItems.length - 1) && (
                      <span
                        className="detail-view-breadcrumb-separator"
                        aria-hidden="true"
                      >
                        &gt;
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </span>
            </div>
          ) : (
            Boolean(swimlaneInfo?.id) &&
            !showDetailedView?.show && (
              <div className="swimlane-name" onClick={handleBackClick}>
                {swimlaneInfo?.name}
              </div>
            )
          )}
        </div>
        {showDetailedView?.show && (
          <div className="budget-swimlane-details-nav budget-swimlane-details-nav--with-ai-trigger">
            <div className="swimlane-name detail-view-title">
              <span className="detail-view-title-text">
                {showDetailedView?.selectedItem?.name}
              </span>
              {hasEditAccess?.() && showDetailedView?.selectedItem?.id && (
                <DropDownMenu
                  className="detail-view-title-menu"
                  toggleClass="detail-view-title-toggle"
                  menuClass="detail-view-title-menu-list"
                  toggleIcon={<ExpandIcon className="detail-view-title-icon" />}
                  menuPortal
                  menuOptions={[
                    {
                      value: 'close-detail-view',
                      label: 'Close Detail View',
                      icon: (
                        <Icon
                          name="CloseIcon"
                          className="detail-view-title-menu-close-icon"
                        />
                      ),
                    },
                    {
                      value: 'delete-detail-view',
                      label: 'Delete Detail View...',
                      icon: (
                        <DeleteOutlineIcon className="detail-view-title-menu-icon" />
                      ),
                    },
                  ]}
                  onChange={handleDetailMenuAction}
                />
              )}
            </div>
            <div
              className="detail-formula-bar-portal"
              id="detail-formula-bar-portal"
            />
            <div className="detail-view-header-actions">
              <button
                type="button"
                aria-label="Manage detail columns"
                aria-controls="detail-column-manager"
                aria-expanded={isDetailColumnManagerOpen}
                ref={detailColumnManagerTriggerRef}
                className={[
                  'detail-column-manager-trigger',
                  'detail-view-column-manager-trigger',
                  isDetailColumnManagerOpen ? 'is-active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setIsDetailColumnManagerOpen(isOpen => !isOpen)}
              >
                <Icon name="DetailsIcon" />
                <span className="detail-column-manager-trigger-label">
                  Columns
                </span>
                {detailColumnManagerMeta.hiddenColumnCount > 0 && (
                  <span className="detail-column-manager-trigger-count">
                    {detailColumnManagerMeta.hiddenColumnCount}
                    <Icon name="VisibilityOffIcon" />
                  </span>
                )}
              </button>
              <button
                type="button"
                className="show-ai-rate-card"
                aria-label={`${
                  showAiInsights ? 'Close' : 'Open'
                } budget side panel`}
                aria-pressed={showAiInsights}
                title={`${showAiInsights ? 'Close' : 'Open'} budget side panel`}
                onClick={() => setShowAiInsights(isOpen => !isOpen)}
              >
                <span
                  aria-hidden="true"
                  className="show-ai-rate-card__panel-icon"
                />
              </button>
            </div>
          </div>
        )}
        {!showDetailedView?.show && (
          <div className="budget-side-panel-header-actions">
            <button
              type="button"
              className="show-ai-rate-card"
              aria-label={`${
                showAiInsights ? 'Close' : 'Open'
              } budget side panel`}
              aria-pressed={showAiInsights}
              title={`${showAiInsights ? 'Close' : 'Open'} budget side panel`}
              onClick={() => setShowAiInsights(isOpen => !isOpen)}
            >
              <span
                aria-hidden="true"
                className="show-ai-rate-card__panel-icon"
              />
            </button>
          </div>
        )}
      </div>
    );
  };
  const getNavigableSwimlaneSibling = (element, direction) => {
    let sibling =
      direction === 'next'
        ? element?.nextElementSibling
        : element?.previousElementSibling;

    while (sibling) {
      if (sibling.classList?.contains('swimlane-item') && sibling.id) {
        return sibling;
      }

      sibling =
        direction === 'next'
          ? sibling.nextElementSibling
          : sibling.previousElementSibling;
    }

    return null;
  };

  const resetTopLevelCategoryScroll = useCallback(() => {
    const swimlaneBlock = swimlaneBlockRef.current;
    if (!swimlaneBlock || swimlaneBlock.scrollLeft === 0) return;

    swimlaneBlock.scrollLeft = 0;
  }, []);

  const queueTopLevelCategoryScrollReset = useCallback(() => {
    if (typeof window === 'undefined') {
      resetTopLevelCategoryScroll();
      return;
    }

    window.requestAnimationFrame(resetTopLevelCategoryScroll);
  }, [resetTopLevelCategoryScroll]);

  const shouldResetTopLevelCategoryScroll = useCallback(
    (source, destination) => {
      const sourceColumnIndex = getCategoryColumnIndex(source?.droppableId);
      const destinationColumnIndex = getCategoryColumnIndex(
        destination?.droppableId
      );

      return sourceColumnIndex === 0 || destinationColumnIndex === 0;
    },
    []
  );

  const resetScrollAfterCategoryDrag = useCallback(
    result => {
      if (
        shouldResetTopLevelCategoryScroll(result?.source, result?.destination)
      ) {
        queueTopLevelCategoryScrollReset();
      }
    },
    [queueTopLevelCategoryScrollReset, shouldResetTopLevelCategoryScroll]
  );

  const handleCategoryDragStart = useCallback(
    start => {
      clearCategoryNestHover();
      startCategoryDragPointerTracking(start);
      resetScrollAfterCategoryDrag(start);
    },
    [
      clearCategoryNestHover,
      resetScrollAfterCategoryDrag,
      startCategoryDragPointerTracking,
    ]
  );

  const handleCategoryDragUpdate = useCallback(
    update => {
      if (update?.combine) {
        armCategoryNestHover(update.combine);
      } else if (categoryDragPointerRef.current.point) {
        updateCategoryNestHoverFromPoint(categoryDragPointerRef.current.point);
      } else {
        clearCategoryNestHover();
      }
      resetScrollAfterCategoryDrag(update);
    },
    [
      armCategoryNestHover,
      clearCategoryNestHover,
      resetScrollAfterCategoryDrag,
      updateCategoryNestHoverFromPoint,
    ]
  );

  const getCategoryDragGroup = useCallback((columnRef, draggableId) => {
    const selectedGroup = columnRef?.getSelectedCategoryGroup?.(draggableId);
    if (selectedGroup?.isGrouped) {
      return selectedGroup;
    }

    const item = getCategoryFromDraggableId(columnRef, draggableId);
    if (!item?.id) return null;

    return {
      ids: [item.id?.toString()],
      isGrouped: false,
      isSequential: true,
      items: [item],
      rowIndexes: [],
      total: Number(item?.cost) || 0,
    };
  }, []);

  const closeMovedCategoryGroupDescendants = useCallback(
    items => {
      (items || []).forEach(item => closeMovedCategoryDescendants(item));
    },
    [closeMovedCategoryDescendants]
  );

  const handleCategoryNestDrop = useCallback(
    async (result, nestTarget) => {
      const sourceColumnIndex = getCategoryColumnIndex(
        result.source?.droppableId
      );
      const targetColumnIndex = getCategoryColumnIndex(nestTarget?.droppableId);

      if (sourceColumnIndex === null || targetColumnIndex === null) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const sourceColumnRef = refs.current[sourceColumnIndex];
      const targetColumnRef = refs.current[targetColumnIndex];
      const movedItem = getCategoryFromDragLocation(
        sourceColumnRef,
        result.source,
        result.draggableId
      );
      const dragGroup = getCategoryDragGroup(
        sourceColumnRef,
        result.draggableId
      );
      const targetItem = getCategoryFromDraggableId(
        targetColumnRef,
        nestTarget?.draggableId
      );

      if (!movedItem?.id || !targetItem?.id || !dragGroup?.items?.length) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const movedItemIds = new Set(
        dragGroup.items.map(item => item.id?.toString())
      );
      if (movedItemIds.has(targetItem.id?.toString())) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const destinationPosition = getCategoryChildCount(targetItem);
      const payload = dragGroup.isGrouped
        ? {
            categories: dragGroup.items.map(item => item.id),
            parent: targetItem.id,
            section: null,
            position: destinationPosition,
          }
        : {
            category: movedItem.id,
            parent: targetItem.id,
            section: null,
            position: destinationPosition,
          };
      const moveMutation = dragGroup.isGrouped
        ? moveLineItemCategoriesMutation
        : moveLineItemCategoryMutation;
      const expectedMovedItems = dragGroup.isGrouped
        ? dragGroup.items
        : [movedItem];

      clearStoredNavigationSnapshot(budgetId);

      try {
        const response = await moveMutation.mutateAsync(payload);
        const movedCategories = Array.isArray(response?.data)
          ? response.data
          : [response?.data || response].filter(Boolean);

        const movedCategoryParentMismatch = movedCategories.some(
          movedCategory =>
            movedCategory?.id && !idsMatch(movedCategory.parent, targetItem.id)
        );

        if (movedCategoryParentMismatch) {
          throw new Error('Category move did not persist the target parent.');
        }

        sourceColumnRef?.refetch?.();
        sourceColumnRef?.clearCategorySelection?.();
        if (targetColumnIndex !== sourceColumnIndex) {
          targetColumnRef?.refetch?.();
        }

        const targetChildColumnRef = refs.current[targetColumnIndex + 1];
        const targetChildColumn = targetChildColumnRef?.getColumnContext?.();
        if (idsMatch(targetChildColumn?.selectedItem?.id, targetItem.id)) {
          targetChildColumnRef?.refetch?.();
        }

        closeMovedCategoryGroupDescendants(expectedMovedItems);
        refetchBudgetId?.();
        refetchSwimlaneCosts?.({ budgetId });
      } catch {
        toast.error(
          'Could not save the category move. Please refresh and try again.',
          toastStyle
        );
        sourceColumnRef?.refetch?.();
        targetColumnRef?.refetch?.();
        refetchBudgetId?.();
      } finally {
        resetScrollAfterCategoryDrag(result);
      }
    },
    [
      budgetId,
      closeMovedCategoryGroupDescendants,
      getCategoryDragGroup,
      moveLineItemCategoriesMutation,
      moveLineItemCategoryMutation,
      refetchBudgetId,
      refetchSwimlaneCosts,
      resetScrollAfterCategoryDrag,
    ]
  );

  const focusSwimlaneElement = element => {
    if (!element?.id) return false;

    const swimId = element.id?.split(',')[0];
    const id = element.id?.split(',')[1];
    const item = refs.current[swimId]?.items?.()?.[id];

    if (!item) return false;

    element.focus();
    refs.current[swimId]?.handleItemClick?.(item)();
    return true;
  };

  const handleKeyUp = e => {
    const newKeyHistory = [...keyHistory];
    const currentSwimlaneItem = e.target?.closest?.('.swimlane-item');

    if (e.key === 'ArrowDown') {
      focusSwimlaneElement(
        getNavigableSwimlaneSibling(currentSwimlaneItem, 'next')
      );
    } else if (e.key === 'ArrowUp') {
      focusSwimlaneElement(
        getNavigableSwimlaneSibling(currentSwimlaneItem, 'previous')
      );
    } else if (e.key === 'ArrowRight') {
      const name = e.target.id;
      const nswimlaneIndex = parseInt(name.split(',')?.[0]) + 1;
      // console.log('nswimlaneIndex', nswimlaneIndex);
      setIdCat(nswimlaneIndex);
      const sid = [nswimlaneIndex, 0].join(',');
      const next = document.getElementById(sid);
      if (next) {
        const id = next.id?.split(',')[1];
        next.focus();
        const item = refs.current[nswimlaneIndex].items()[id];
        refs.current[nswimlaneIndex].handleItemClick(item)();
        newKeyHistory.push(e.target.id);
      } else {
        newKeyHistory.push(e.target.id);
        const el = document.querySelector('.item input');
        if (el) {
          el?.focus();
        } else {
          document.querySelector('.action-add-details')?.click();
        }
      }
    } else if (e.key === 'ArrowLeft') {
      const previousKey = newKeyHistory.pop();
      if (previousKey) {
        const previousElement = document.getElementById(previousKey);
        if (previousElement) {
          previousElement.focus();
          const swimId = previousKey.split(',')[0];
          const id = previousKey.split(',')[1];
          setIdCat(id);
          const item = refs.current[swimId].items()[id];
          refs.current[swimId].handleItemClick(item)();
        }
      } else {
        const name = e.target.id;
        const nswimlaneIndex = parseInt(name.split(',')?.[0]) - 1;
        if (nswimlaneIndex >= 0) {
          setIdCat(nswimlaneIndex);
          const sid = [nswimlaneIndex, 0].join(',');
          const next = document.getElementById(sid);
          if (next) {
            const id = next.id?.split(',')[1];
            next.focus();
            const item = refs.current[nswimlaneIndex].items()[id];
            refs.current[nswimlaneIndex].handleItemClick(item)();
          }
        }
      }
    }

    setKeyHistory(newKeyHistory);
  };

  const handleCategoryDragEnd = useCallback(
    async result => {
      const nestHoverState = categoryNestHoverRef.current;
      const nestTarget = result.combine || nestHoverState.target;
      const nestTargetKey = getCategoryNestTargetKey(nestTarget);
      const shouldNestCategory =
        nestTargetKey &&
        nestHoverState.key === nestTargetKey &&
        nestHoverState.ready;

      clearCategoryNestHover();
      stopCategoryDragPointerTracking();

      if (shouldNestCategory) {
        await handleCategoryNestDrop(result, nestTarget);
        return;
      }

      if (result.combine) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      if (!result.destination) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const sourceColumnIndex = getCategoryColumnIndex(
        result.source?.droppableId
      );
      const destinationColumnIndex = getCategoryColumnIndex(
        result.destination?.droppableId
      );

      if (sourceColumnIndex === null || destinationColumnIndex === null) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      if (sourceColumnIndex === destinationColumnIndex) {
        refs.current[sourceColumnIndex]?.handleDragEnd?.(result);
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const destinationColumn =
        refs.current[destinationColumnIndex]?.getColumnContext?.();
      const sourceColumnRef = refs.current[sourceColumnIndex];
      const destinationColumnRef = refs.current[destinationColumnIndex];
      const sourceColumn = sourceColumnRef?.getColumnContext?.();
      const sourceRows = getColumnRows(sourceColumnRef);
      const movedItem = getCategoryFromDragLocation(
        sourceColumnRef,
        result.source,
        result.draggableId
      );
      const dragGroup = getCategoryDragGroup(
        sourceColumnRef,
        result.draggableId
      );

      if (!movedItem?.id || !destinationColumn || !dragGroup?.items?.length) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const destinationParentId = getDestinationParentId(destinationColumn);
      const movedItemIds = new Set(
        dragGroup.items.map(item => item.id?.toString())
      );
      if (
        destinationParentId &&
        movedItemIds.has(destinationParentId?.toString())
      ) {
        resetScrollAfterCategoryDrag(result);
        return;
      }

      const isDestinationTopLevel = destinationParentId === null;
      const isSourceTopLevel = getDestinationParentId(sourceColumn) === null;
      const destinationRows = getColumnRows(destinationColumnRef);
      const destinationSectionId = isDestinationTopLevel
        ? getDestinationSectionId(destinationRows, result.destination.index)
        : null;
      const destinationPosition = getDestinationPosition({
        rows: destinationRows,
        destinationIndex: result.destination.index,
        destinationSectionId,
        isTopLevel: isDestinationTopLevel,
      });

      if (dragGroup.isGrouped) {
        const payload = {
          categories: dragGroup.items.map(item => item.id),
          parent: destinationParentId,
          section: isDestinationTopLevel ? destinationSectionId : null,
          position: destinationPosition,
        };

        clearStoredNavigationSnapshot(budgetId);
        closeMovedCategoryGroupDescendants(dragGroup.items);

        try {
          const response = await moveLineItemCategoriesMutation.mutateAsync(
            payload
          );
          const movedCategories = Array.isArray(response?.data)
            ? response.data
            : [response?.data || response].filter(Boolean);

          const parentMismatch = movedCategories.some(
            movedCategory =>
              movedCategory?.id &&
              !idsMatch(movedCategory.parent, destinationParentId)
          );
          const sectionMismatch =
            isDestinationTopLevel &&
            movedCategories.some(
              movedCategory =>
                movedCategory?.id &&
                !idsMatch(movedCategory.section, destinationSectionId)
            );

          if (parentMismatch || sectionMismatch) {
            throw new Error('Category group move did not persist.');
          }

          sourceColumnRef?.clearCategorySelection?.();
          sourceColumnRef?.refetch?.();
          destinationColumnRef?.refetch?.();
          refetchBudgetId?.();
          refetchSwimlaneCosts?.({ budgetId });
        } catch {
          toast.error(
            'Could not save the category move. Please refresh and try again.',
            toastStyle
          );
          sourceColumnRef?.refetch?.();
          destinationColumnRef?.refetch?.();
          refetchBudgetId?.();
        } finally {
          resetScrollAfterCategoryDrag(result);
        }
        return;
      }

      const destinationSwimlaneId = getDestinationSwimlaneId(destinationColumn);
      const sourceItems = sourceColumnRef?.items?.() || [];
      const destinationItems = destinationColumnRef?.items?.() || [];
      const nextSourceItems = getOptimisticSourceItems({
        isTopLevel: isSourceTopLevel,
        items: sourceItems,
        movedItem,
        rows: sourceRows,
      });
      const optimisticMovedItem = {
        ...movedItem,
        parent: destinationParentId,
        section: isDestinationTopLevel ? destinationSectionId : null,
        position: destinationPosition,
        ...(destinationSwimlaneId ? { swimlane: destinationSwimlaneId } : {}),
      };
      const nextDestinationItems = getOptimisticDestinationItems({
        destinationItems,
        destinationPosition,
        destinationRows,
        isTopLevel: isDestinationTopLevel,
        movedItem,
        optimisticMovedItem,
      });

      const payload = {
        category: movedItem.id,
        parent: destinationParentId,
        section: isDestinationTopLevel ? destinationSectionId : null,
        position: destinationPosition,
      };

      clearStoredNavigationSnapshot(budgetId);
      sourceColumnRef?.setItems?.(nextSourceItems);
      destinationColumnRef?.setItems?.(nextDestinationItems);
      closeMovedCategoryDescendants(movedItem);

      try {
        const response = await moveLineItemCategoryMutation.mutateAsync(
          payload
        );
        const movedCategory = response?.data || response;

        if (
          movedCategory?.id &&
          !idsMatch(movedCategory.parent, destinationParentId)
        ) {
          throw new Error('Category move did not persist the target parent.');
        }

        if (
          isDestinationTopLevel &&
          movedCategory?.id &&
          !idsMatch(movedCategory.section, destinationSectionId)
        ) {
          throw new Error('Category move did not persist the target section.');
        }

        sourceColumnRef?.refetch?.();
        destinationColumnRef?.refetch?.();
        refetchBudgetId?.();
        refetchSwimlaneCosts?.({ budgetId });
      } catch {
        toast.error(
          'Could not save the category move. Please refresh and try again.',
          toastStyle
        );
        sourceColumnRef?.refetch?.();
        destinationColumnRef?.refetch?.();
        refetchBudgetId?.();
      } finally {
        resetScrollAfterCategoryDrag(result);
      }
    },
    [
      budgetId,
      clearCategoryNestHover,
      closeMovedCategoryGroupDescendants,
      closeMovedCategoryDescendants,
      getCategoryDragGroup,
      handleCategoryNestDrop,
      moveLineItemCategoriesMutation,
      moveLineItemCategoryMutation,
      refetchBudgetId,
      refetchSwimlaneCosts,
      resetScrollAfterCategoryDrag,
      stopCategoryDragPointerTracking,
    ]
  );

  useEffect(() => {
    refs.current = refs.current.slice(0, swimlanes.length);
  }, [swimlanes.length]);

  return (
    <div
      className={`budget-swimlane-block${
        isViewportResizing ? ' budget-swimlane-block--viewport-resizing' : ''
      }`}
      ref={swimlaneBlockRef}
    >
      <BudgetSearchOverlay
        budgetId={budgetId}
        onSelectResult={handleBudgetSearchResultSelect}
      />
      {renderSwimlaneNav()}
      <div
        className={`budget-swimlane-wrapper${
          showAiInsights && !showDetailedView?.show
            ? ' budget-swimlane-wrapper--with-side-panel'
            : ''
        }`}
        ref={swimlaneWrapperRef}
      >
        <DragDropContext
          autoScrollerOptions={CATEGORY_DRAG_AUTO_SCROLLER_OPTIONS}
          onDragStart={handleCategoryDragStart}
          onDragUpdate={handleCategoryDragUpdate}
          onDragEnd={handleCategoryDragEnd}
        >
          <div className="budget-swimlane-container">
            {swimlanes?.map((swimlaneItems, swimlaneIndex) => {
              const suggestionScopeKey =
                swimlaneIndex === 0
                  ? 'category:root'
                  : `category:${swimlaneItems.selectedItem?.id || ''}`;
              return (
                <SwimlaneRefactored
                  key={`swimlane-${swimlaneIndex}-${swimlaneItems.selectedItem?.id}`}
                  ref={el => (refs.current[swimlaneIndex] = el)}
                  swimlaneHeight={swimlaneHeight}
                  budgetId={budgetId}
                  idCat={idCat}
                  swimlaneIndex={swimlaneIndex}
                  droppableId={buildCategoryDroppableId(swimlaneIndex)}
                  categoryNestHoverKey={categoryNestHoverKey}
                  categoryNestReadyKey={categoryNestReadyKey}
                  activeSelectionColumnIndex={activeCategorySelectionColumn}
                  onCategorySelectionActivated={
                    setActiveCategorySelectionColumn
                  }
                  onColumnWidthChange={setNavWidth}
                  contextualCategorySuggestionRequest={
                    contextualCategorySuggestionRequest
                  }
                  suggestionScopeKey={suggestionScopeKey}
                  onSuggestionDeleted={onSuggestionDeleted}
                  onCategoryColumnItemsChange={onCategoryColumnItemsChange}
                  onKeyUp={handleKeyUp}
                  handleAddSwimLanes={addSwimLanes}
                  editing={editing}
                  setEditing={setEditing}
                  // Clear swimlane nav & reset columns when a top-level category is deleted
                  clearSwimlaneInfo={
                    swimlaneIndex === 0
                      ? handleTopLevelSwimlaneDeleted
                      : undefined
                  }
                  addDetailedView={addDetailedView}
                  presenceBreadcrumbs={presenceBreadcrumbs}
                  budgetData={budgetData}
                  {...swimlaneItems}
                  addItemSuggestions={
                    swimlaneIndex === 0 && !isAicpTemplate
                      ? addItemSuggestions
                      : null
                  }
                />
              );
            })}
          </div>
        </DragDropContext>
      </div>
      {showDetailedView?.show && (
        <div
          className="budget-swimlane-details-wrapper"
          ref={swimlaneDetailsWrapperRef}
          onMouseDownCapture={onDetailInteraction}
          onKeyDownCapture={onDetailInteraction}
        >
          <div className="budget-swimlane-details-container">
            <DetailedViewRefactored
              setNavWidth={setNavWidth}
              onKeyUp={handleKeyUp}
              key={`Detail-${selectedBudget?.budget}-${showDetailedView?.selectedItem?.budget}`}
              newDetailViewAdded={newDetailViewAdded}
              setNewDetailViewAdded={setNewDetailViewAdded}
              restoreCellState={restoreCellState}
              cellPresences={cellPresences}
              onDetailCellFocus={handleDetailCellFocus}
              onSuggestionDeleted={onSuggestionDeleted}
              onRateContextChange={scheduleAiInsightsRefresh}
              isColumnManagerOpen={isDetailColumnManagerOpen}
              onColumnManagerOpenChange={setIsDetailColumnManagerOpen}
              onColumnManagerMetaChange={setDetailColumnManagerMeta}
              mentionableUsers={mentionableUsers}
            />
          </div>
        </div>
      )}
      <AiInsightsPanel
        budgetId={selectedBudget?.budget}
        categoryId={
          showDetailedView?.show ? showDetailedView?.selectedItem?.id : null
        }
        focusItems={
          showDetailedView?.show &&
          aiInsightsRefresh.categoryId?.toString() ===
            showDetailedView?.selectedItem?.id?.toString()
            ? aiInsightsRefresh.focusItems
            : []
        }
        onBudgetChange={handleAssistantBudgetChange}
        onHide={() => setShowAiInsights(false)}
        open={showAiInsights}
        rateCardAvailable={Boolean(
          showDetailedView?.show && showDetailedView?.selectedItem?.id
        )}
        refreshKey={aiInsightsRefresh.revision}
        mentionableUsers={mentionableUsers}
        requestedTab={
          deepLinkViewState?.panel?.open ? deepLinkViewState.panel.tab : null
        }
      />
      <DeleteConfirmModal
        showModal={showDeleteDetailConfirm}
        handleOnClose={hideDeleteDetailConfirm}
        onCancel={hideDeleteDetailConfirm}
        onConfirm={handleDeleteDetailView}
        confirmText="Are you sure you want to delete"
        itemName={showDetailedView?.selectedItem?.name || 'this detail view'}
        confirmLabel="Delete"
        disabled={isDeletingDetailView}
      />
    </div>
  );
};

SwimlaneLayout.propTypes = {
  swimlaneHeight: PropTypes.number,
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  presenceBreadcrumbs: PropTypes.array,
  cellPresences: PropTypes.array,
  changeEvents: PropTypes.array,
  isLoadingChangeEvents: PropTypes.bool,
  contextualCategorySuggestionRequest: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    targetCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    targetColumnIndex: PropTypes.number.isRequired,
  }),
  onDetailInteraction: PropTypes.func,
  onSuggestionDeleted: PropTypes.func,
  onCategoryColumnItemsChange: PropTypes.func,
  mentionableUsers: PropTypes.array,
};

SwimlaneLayout.defaultProps = {
  swimlaneHeight: 200,
  presenceBreadcrumbs: [],
  cellPresences: [],
  changeEvents: [],
  isLoadingChangeEvents: false,
  contextualCategorySuggestionRequest: null,
  onDetailInteraction: null,
  onSuggestionDeleted: null,
  onCategoryColumnItemsChange: null,
  mentionableUsers: [],
};
export default SwimlaneLayout;
